import React, { startTransition, useEffect, useMemo, useState } from 'react';
import { useTranslation } from '../hooks/useTranslation.js';
import { requestForm, requestJson, fetchCurrentUser } from '../utils/authClient.js';
import { resolveAdminServiceLink } from '../utils/adminServiceLinks.mjs';
import { localizePath } from '../utils/localizedRoutes.js';
import { AdminLabelsContext, AdminPageHead, AdminSection, AdminSideNav, ShowMore, fill, readAdminRoute, readAdminStored, useShowMore, writeAdminRoute, writeAdminStored } from '../components/AdminNav.jsx';
import { AdminLink, FactGrid, GpuPanel, displayModelName, formatBerlin, startOfBerlinDay } from './VeridionShared.jsx';
import VeridionPage from './VeridionPage.jsx';
import VeridionPaioneerPage from './VeridionPaioneerPage.jsx';
import VeridionGameTheoryPanel from './VeridionGameTheoryPanel.jsx';
import { LlmConsole } from './VeridionCorePanel.jsx';
import CompanyMemoryPage from './CompanyMemoryPage.jsx';
import { MemoryDay } from './CompanyMemoryDay.jsx';
import AgenticRagPage from './AgenticRagPage.jsx';
import SoftwareDevelopmentPage from './SoftwareDevelopmentPage.jsx';
import LlmPaperPage from './LlmPaperPage.jsx';
import TrainingDataFactoryPage from './TrainingDataFactoryPage.jsx';
import SystemLlmAllocationPanel from '../components/SystemLlmAllocationPanel.jsx';
import VeridionMailboxesPanel from './VeridionMailboxesPanel.jsx';
import { VeridionConversationsPanel } from './VeridionOrganisationPanel.jsx';

// The pages of the organisation group and the former addresses of the dashboard: an old link (`#overview`,
// `#veridion-paioneer/organisation`, `#envoy/usage`, ...) opens the page that holds its content today
// (IDD D-HP-ADMIN-040), and the address is rewritten to the new one without a second history entry.
const ORGANISATION_PAGES = ['chart', 'colleagues', 'voices', 'people', 'approvals', 'edit'];
const legacyRoute = ({ tab, section, detail }) => {
  if (String(tab || '').startsWith('datalake-')) {
    return { tab: 'training', section: '', detail: '' };
  }
  // The former demos sub-pages for the model architecture live on the paper page now (IDD D-HP-ADMIN-062).
  if (tab === 'demos' && ['architecture', 'lineage', 'benchmarks'].includes(section)) {
    return { tab: 'llm', section: '', detail: '' };
  }
  const map = {
    overview: () => ['today', '', ''],
    platform: () => (section === 'workloads' ? ['workloads', '', ''] : null),
    envoy: () => ({ routing: ['gateway', '', ''], engines: ['engines', '', ''], budgets: ['usage', 'budgets', ''], usage: ['usage', 'identities', ''] }[section] || ['gateway', '', '']),
    agentic: () => (section === 'usage' ? ['usage', 'smtm', ''] : null),
    'veridion-paioneer': () => ({
      overview: ['core', '', ''],
      organisation: ['organisation', ORGANISATION_PAGES.includes(detail) ? detail : 'chart', ''],
      webex: ['webex', '', ''],
      agents: ['agents', 'agents', ''],
      mailboxes: ['mailboxes', '', ''],
      definitions: ['agents', 'definitions', ''],
      builds: ['gitlab', 'builds', ''],
      coding: ['coding', '', ''],
      gitlab: ['gitlab', 'gitlab', ''],
      mcp: ['orchestrator', 'mcp', ''],
      knowledge: ['knowledge', '', ''],
      usage: ['usage', 'company', ''],
      activity: ['orchestrator', 'activity', ''],
    }[section] || ['today', '', '']),
  };
  const found = map[tab] ? map[tab]() : null;
  return found ? { tab: found[0], section: found[1], detail: found[2] } : null;
};
const hashOf = ({ tab, section, detail }) => `#${encodeURIComponent(tab)}${section ? `/${encodeURIComponent(section)}` : ''}${section && detail ? `/${encodeURIComponent(detail)}` : ''}`;
const currentRoute = () => {
  const route = readAdminRoute();
  const moved = route.tab ? legacyRoute(route) : null;
  if (moved) window.history.replaceState(null, '', hashOf(moved));
  return moved || route;
};

// The Keycloak realm's smtpServer record, which sends registration,
// verification and reset mail. SMTP only; the realm has no IMAP side.
const mailTemplate = {
  smtp_host: '',
  smtp_port: '',
  smtp_security: 'ssl_tls',
  smtp_username: '',
  smtp_password: '',
  from_email: '',
  from_name: '',
  reply_to_email: '',
  configured: false,
  source: '',
};

const createUserTemplate = {
  email: '',
  username: '',
  password: '',
  roles: ['user'],
  status: 'pending_mfa',
};

// State of the Envoy AI Gateway: routed models, serving backends and the token
// budget of each identity, counted in Redis over a fixed window.
const aiGatewayTemplate = {
  available: false,
  error: '',
  gateway: { name: '', namespace: '', programmed: false, accepted: false, url: '' },
  identity: { claim: '', header: '' },
  models: [],
  budget: { window: 'Hour', default_tokens: null, rules: [] },
  budget_source: '',
};

const aiUsageTemplate = {
  available: false,
  error: '',
  window: 'Hour',
  window_started_at: null,
  summary: { identities: 0, total_tokens: 0 },
  identities: [],
};

const sessionSummaryTemplate = {
  generated_at: null,
  summary: {
    active_sessions: 0,
    active_session_users: 0,
    active_mfa_sessions: 0,
    active_mfa_session_users: 0,
    total_users: 0,
  },
};

const servicesTemplate = {
  error: '',
  services: [],
};

// Every pAIoneer_Labs_GmbH workload as Kubernetes reports it.
const platformTemplate = {
  available: false,
  error: '',
  generated_at: null,
  metrics_available: false,
  summary: { workloads: 0, ready: 0, degraded: 0, restarts: 0, cpu_cores: 0, memory_bytes: 0 },
  nodes: [],
  events: [],
  namespaces: [],
};

const smtmStatusTemplate = {
  error: '',
  generated_at: null,
  thresholds: { minimum_training_rows: 100 },
  summary: {
    dataframes_total: 0,
    base_frames: 0,
    trainable_frames: 0,
    waiting_for_history: 0,
    prediction_enabled_frames: 0,
    prediction_disabled_frames: 0,
    ml_frames: 0,
    prediction_frames: 0,
    stale_predictions: 0,
    rss_frames: 0,
    orphan_ml_frames: 0,
    orphan_prediction_frames: 0,
    online_models: 0,
    models_with_artifacts: 0,
    blob_total: 0,
    prediction_gap_frames: 0,
  },
  azurite: {
    available: false,
    configured: false,
    error: '',
    summary: {
      latest_upload_at: null,
    },
  },
  runtime: {
    latest_model_upload_at: null,
    latest_prediction_at: null,
    hints: [],
  },
  frames: [],
  rss_feeds: [],
};

const smtmReportingTemplate = {
  error: '',
  generated_at: null,
  summary: {
    qualified_signals: 0,
    resolved_signals: 0,
    open_signals: 0,
    wins: 0,
    losses: 0,
    win_rate: null,
    average_expected_return_pct: null,
    average_actual_return_pct: null,
    net_actual_return_pct: null,
    delivery_total: 0,
    delivery_sent: 0,
    delivery_pending: 0,
    delivery_failed: 0,
  },
  recent_signals: [],
  recent_delivery_failures: [],
  recent_pending_deliveries: [],
};

const smtmTrainingTemplate = {
  error: '',
  generated_at: null,
  summary: {
    model_count: 0,
    online_models: 0,
    failed_models: 0,
    stale_models: 0,
    average_mae: null,
    average_rmse: null,
    average_r2: null,
    average_mase: null,
    average_directional_accuracy: null,
  },
  models: [],
};

const smtmTrainingConsoleTemplate = {
  error: '',
  generated_at: null,
  lines: [],
};

const smtmPredictionSettingsTemplate = {
  error: '',
  generated_at: null,
  summary: {
    tracked_instruments: 0,
    prediction_enabled: 0,
    prediction_disabled: 0,
  },
  instruments: [],
};

const smtmPredictionRuntimeTemplate = {
  error: '',
  generated_at: null,
  runtime: {
    forecast_horizon_minutes: 30,
    resolution_minutes: 30,
    require_completed_bars: true,
    prediction_gate_open: true,
    rebuild_active: false,
    prediction_schema_version: 2,
    feature_schema_version: 2,
    news_feature_schema_version: 2,
    config_version: 1,
  },
};

const userActionTemplate = {};
const sectionLoadingTemplate = {
  platform: true,
  mailSettings: true,
  users: true,
  audit: true,
  sessionSummary: true,
  aiUsage: true,
  aiGateway: true,
  services: true,
  smtmPredictionRuntime: true,
  smtmPredictionSettings: true,
  smtmTrainingConsole: true,
  smtmStatus: true,
  smtmReporting: true,
  smtmTraining: true,
};
const errorKeyMap = {
  ADMIN_REQUIRED: 'adminRequired',
  USER_EXISTS: 'userExists',
  USER_CREATE_INVALID: 'userCreateInvalid',
  PASSWORD_TOO_WEAK: 'passwordTooWeak',
  STATUS_INVALID: 'userCreateInvalid',
  LAST_ADMIN_PROTECTED: 'lastAdminProtected',
  USER_NOT_FOUND: 'registrationFailed',
  MAIL_NOT_CONFIGURED: 'mailNotConfigured',
  AI_BUDGET_INVALID: 'unknownError',
  AI_TOKEN_LIMIT_INVALID: 'unknownError',
  AI_USAGE_RATE_UNAVAILABLE: 'unknownError',
};
// Roles and states come from Keycloak: /api/admin/users reports which realm
// roles exist and which states can be written. These constants are the
// assumption until that answer arrives.
const fallbackRoles = ['user', 'admin'];
const fallbackStatuses = ['active', 'disabled'];

const normaliseRoleList = (roles, options = fallbackRoles) => {
  const values = Array.isArray(roles) ? roles : String(roles || '').split(',');
  const known = options.length ? options : fallbackRoles;
  const selected = new Set(values.map((role) => role.trim()).filter((role) => known.includes(role)));
  selected.add('user');
  return known.filter((role) => selected.has(role));
};

const toggleRole = (roles, role, checked, options = fallbackRoles) => {
  const selected = new Set(normaliseRoleList(roles, options));
  if (checked) selected.add(role);
  if (!checked && role !== 'user') selected.delete(role);
  selected.add('user');
  return (options.length ? options : fallbackRoles).filter((availableRole) => selected.has(availableRole));
};

const serialiseRoles = (roles, options = fallbackRoles) => normaliseRoleList(roles, options).join(',');

// The app host restores only application addresses after its own first
// login; a direct call of its instance page would land in its application.
// One silent request establishes the app host's session, then the page opens.
const openAppHostPage = async (event) => {
  event.preventDefault();
  try {
    await fetch('/shinyproxy/', { credentials: 'same-origin', redirect: 'follow' });
  } catch (error) {
    // The page signs in itself if the silent request did not get through.
  }
  window.location.assign('/shinyproxy/admin');
};

// The two company switches on the Today page (owner UX): read from the Core's overview and switched over the same
// doors the Core panel uses. On autopilot there is no human decision (IDD D-VCORE-295); the emergency stop is its
// own function and has nothing to do with the autopilot.
const TodayControls = ({ t, title }) => {
  const c = t.admin?.core || {};
  const o = t.admin?.org || {};
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => requestJson('/api/admin/veridion-core/overview')
    .then((result) => setInfo(result?.core || {})).catch(() => setInfo({}));
  useEffect(() => { load(); }, []);
  const toggle = (path, body, question) => {
    if (question && !window.confirm(question)) return;
    setBusy(true);
    // The switch is a POST of the door's body; a bare options object would send a GET that changes nothing.
    requestJson(path, { method: 'POST', body: JSON.stringify(body) }).then(load).catch(() => null).finally(() => setBusy(false));
  };
  if (!info) return null;
  const units = info.autopilotUnits || [];
  const activeUnits = units.filter((u) => u.on);
  const autopilotState = info.autopilot
    ? (units.length && activeUnits.length < units.length
      ? `${c.autopilotUnitsTitle || c.autopilotActive}: ${activeUnits.map((u) => u.prefix || u.name).join(' · ')}`
      : c.autopilotActive)
    : '';
  return (
    <AdminSection id="today-controls" title={title}
      state={[autopilotState, info.emergencyStop ? c.emergencyActive : ''].filter(Boolean).join(' · ')}
      tone={info.emergencyStop ? 'error' : 'ok'}>
      <div className="space-y-3">
        <div className="admin-autopilot-master">
          <button type="button" className={info.autopilot ? 'homepage-button-primary' : 'homepage-button-secondary'} disabled={busy}
            onClick={() => toggle('/api/admin/veridion-core/autopilot', { on: !info.autopilot },
              info.autopilot ? c.confirmAutopilotOff : c.confirmAutopilotOn)}>
            {info.autopilot ? c.autopilotOff : c.autopilotOn}
          </button>
          <button type="button" className={`${info.emergencyStop ? 'homepage-button-primary' : 'homepage-button-secondary'} is-destructive`} disabled={busy}
            onClick={() => toggle('/api/admin/veridion-core/emergency', { on: !info.emergencyStop },
              info.emergencyStop ? c.confirmEmergencyOff : c.confirmEmergencyOn)}>
            {info.emergencyStop ? c.emergencyRelease : c.emergencyStop}
          </button>
        </div>
        {units.length ? (
          <div className="homepage-muted-panel p-3 space-y-2">
            <div className="text-xs font-bold">{c.autopilotUnitsTitle}</div>
            <div className="text-xs text-text-secondary">{c.autopilotUnitsHint}</div>
            <div className="admin-autopilot-grid">
              {units.map((unit) => {
                const labelText = `${unit.name}${unit.prefix ? ` (${unit.prefix})` : ''}`;
                const stateText = `${unit.on ? c.autopilotUnitOn : c.autopilotUnitOff}${unit.status === 'standby' ? ` · ${o.standingStandby || 'standby'}` : ''}`;
                return (
                  <button key={unit.id} type="button"
                    className={`${unit.on ? 'homepage-button-primary' : 'homepage-button-secondary'} admin-autopilot-unit-btn`}
                    disabled={busy}
                    title={`${labelText} · ${stateText}`}
                    onClick={() => toggle('/api/admin/veridion-core/autopilot', { unit: unit.id, on: !unit.on })}>
                    <span className="admin-autopilot-unit-label">{labelText}</span>
                    <span className="admin-autopilot-unit-state">{stateText}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
    </AdminSection>
  );
};

const AdminPage = () => {
  const { t, currentLang } = useTranslation();
  const [me, setMe] = useState(null);
  const [aiGateway, setAiGateway] = useState(aiGatewayTemplate);
  const [gpu, setGpu] = useState({ data: null, error: '' });
  const [mailSettings, setMailSettings] = useState(mailTemplate);
  const [users, setUsers] = useState([]);
  const [aiUsage, setAiUsage] = useState(aiUsageTemplate);
  const [sessionSummary, setSessionSummary] = useState(sessionSummaryTemplate);
  const [services, setServices] = useState(servicesTemplate);
  const [platform, setPlatform] = useState(platformTemplate);
  const [availableRoles, setAvailableRoles] = useState(fallbackRoles);
  const [availableStatuses, setAvailableStatuses] = useState(fallbackStatuses);
  const [budgetDraft, setBudgetDraft] = useState({ default: '', identity: '', tokens: '' });
  // Where a model is served (D-HP-ADMIN-023): the company's GPU, or a cloud provider by its name.
  const sourceLabel = (source) => (source === 'gpu' ? t.admin.aiSourceGpu : source === 'openrouter' ? 'OpenRouter' : source === 'stackit' ? 'STACKIT' : (!source || source === '*') ? '-' : source);
  const [budgetSaving, setBudgetSaving] = useState(false);
  const [smtmPredictionRuntime, setSmtmPredictionRuntime] = useState(smtmPredictionRuntimeTemplate);
  const [smtmPredictionSettings, setSmtmPredictionSettings] = useState(smtmPredictionSettingsTemplate);
  const [smtmPredictionRuntimeDraft, setSmtmPredictionRuntimeDraft] = useState(smtmPredictionRuntimeTemplate.runtime);
  const [smtmPredictionRuntimeSaving, setSmtmPredictionRuntimeSaving] = useState(false);
  const [smtmTrainingConsole, setSmtmTrainingConsole] = useState(smtmTrainingConsoleTemplate);
  const [smtmStatus, setSmtmStatus] = useState(smtmStatusTemplate);
  const [smtmReporting, setSmtmReporting] = useState(smtmReportingTemplate);
  const [smtmTraining, setSmtmTraining] = useState(smtmTrainingTemplate);
  const [smtmPredictionDrafts, setSmtmPredictionDrafts] = useState({});
  const [smtmPredictionSaving, setSmtmPredictionSaving] = useState(false);
  const [audit, setAudit] = useState([]);
  const [createUser, setCreateUser] = useState(createUserTemplate);
  const [userActions, setUserActions] = useState(userActionTemplate);
  const [filter, setFilter] = useState('');
  const [userStatusFilter, setUserStatusFilter] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('');
  const [userPage, setUserPage] = useState(0);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [route, setRoute] = useState(() => currentRoute());
  const [lastSections, setLastSections] = useState({});
  const [attention, setAttention] = useState({ loaded: false, company: null, organisation: null, tasks: null, smtmMcp: null });
  // The company's day for the first page (IDD D-HP-ADMIN-040): the timeline since the administrator's last look, the
  // open work items and the meetings, read with what needs a person.
  const [today, setToday] = useState(() => ({ loaded: false, error: '', since: readAdminStored('lastLook', '') || startOfBerlinDay().toISOString(), timeline: [], workItems: [], meetings: [] }));
  const [auditShown, auditMore] = useShowMore(25);
  const [timelineShown, timelineMore] = useShowMore(25);
  const [workShown, workMore] = useShowMore(20);
  const [eventsShown, eventsMore] = useShowMore(50);
  const [workOwnerFilter, setWorkOwnerFilter] = useState('');
  const [meetingBusy, setMeetingBusy] = useState(false);
  const [meetingNotice, setMeetingNotice] = useState('');
  const [smtmInstrumentFilter, setSmtmInstrumentFilter] = useState('');
  const [selectedSmtmTrainingModelName, setSelectedSmtmTrainingModelName] = useState('');
  const [testRecipient, setTestRecipient] = useState('info@paioneer.eu');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [sectionLoading, setSectionLoading] = useState(sectionLoadingTemplate);
  const translatedError = error ? (errorKeyMap[error] ? t.auth.errors?.[errorKeyMap[error]] || error : error) : '';
  const formatRoles = (roles) => normaliseRoleList(roles, availableRoles).map((role) => t.admin.roleLabels?.[role] || role).join(', ');
  const formatStatus = (status) => t.admin.statusLabels?.[status] || status;
  const formatInteger = (value) => (value === null || value === undefined || value === '' ? '-' : Number(value).toLocaleString());
  const formatTimestamp = formatBerlin;
  const formatMetric = (value, digits = 4, suffix = '') => (value === null || value === undefined || value === '' ? '-' : `${Number(value).toFixed(digits)}${suffix}`);
  const formatPercent = (value) => (value === null || value === undefined || value === '' ? '-' : `${Number(value).toFixed(2)}%`);
  const artifactLabel = (entry) => {
    if (typeof entry === 'string') return entry;
    if (!entry || typeof entry !== 'object') return '-';
    return entry.filename || entry.name || entry.blob_name || entry.path || '-';
  };
  const formatMinutes = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    const minutes = Number(value);
    if (!Number.isFinite(minutes)) return '-';
    if (minutes < 0) return `${Math.abs(minutes)} ${t.admin.smtmMinutesAhead}`;
    if (minutes < 60) return `${minutes} ${t.admin.smtmMinutesAgo}`;
    const hours = Math.floor(minutes / 60);
    if (hours < 48) return `${hours} ${t.admin.smtmHoursAgo}`;
    return `${Math.floor(hours / 24)} ${t.admin.smtmDaysAgo}`;
  };
  const formatBytes = (value) => {
    const bytes = Number(value || 0);
    if (!Number.isFinite(bytes) || bytes <= 0) return '-';
    if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
    if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(0)} MB`;
    return `${(bytes / 1024).toFixed(0)} KB`;
  };
  // An event carries its age, so a start-up hiccup is not read as a current state.
  const eventAgeMinutes = (value) => {
    const stamp = Date.parse(value || '');
    return Number.isFinite(stamp) ? Math.round((Date.now() - stamp) / 60000) : null;
  };
  const formatCores = (value) => (value === null || value === undefined ? '-' : `${Number(value).toFixed(2)}`);
  const platformStateLabel = (state) => ({
    ready: t.admin.platformStateReady,
    degraded: t.admin.platformStateDegraded,
    down: t.admin.platformStateDown,
  }[state] || t.admin.platformStateUnknown);
  const platformStateClass = (state) => (
    state === 'ready' ? 'is-ok' : state === 'degraded' ? 'is-warning' : 'is-missing');
  const smtmStateLabel = (state) => t.admin.smtmStateLabels?.[state] || state || '-';
  const smtmIssueLabel = (issue) => t.admin.smtmIssueLabels?.[issue] || issue;
  const smtmHintLabel = (hint) => {
    const count = Number(hint?.count || 0);
    const label = t.admin.smtmHintLabels?.[hint?.code] || hint?.code || '-';
    return count > 0 ? `${formatInteger(count)} ${label}` : label;
  };
  const smtmStateClass = (state) => {
    if (['available', 'trainable', 'online', 'completed'].includes(state)) return 'is-ok';
    if (['waiting_for_history', 'stale_prediction', 'offline', 'unknown', 'training', 'uploading', 'open'].includes(state)) return 'is-warning';
    return 'is-missing';
  };
  const smtmDeliveryClass = (status) => {
    if (status === 'sent') return 'is-ok';
    if (status === 'pending') return 'is-warning';
    return 'is-missing';
  };
  const resolveDashboardLink = (link) => resolveAdminServiceLink(link, currentLang, localizePath);
  const smtmInstrumentQuery = smtmInstrumentFilter.trim();
  const buildSmtmFilterQuery = (rawValue = smtmInstrumentFilter) => {
    const params = new URLSearchParams();
    const normalized = String(rawValue || '').trim();
    if (normalized) {
      params.set('instrument_names', normalized);
    }
    const serialized = params.toString();
    return serialized ? `?${serialized}` : '';
  };
  // The pages of the dashboard follow the working day of the company (IDD D-HP-ADMIN-040): today, the organisation,
  // the work, the communication, the memory, then the platform underneath, the products and the identity. A page
  // with several subjects lists them as sections; the organisation group lists its pages flat.
  const n = t.admin.nav || {};
  const a = n.attention || {};
  const tabLabels = {
    today: n.pages?.today,
    organisation: n.groups?.organisation,
    gametheory: n.pages?.gametheory,
    demos: n.pages?.demos,
    llm: n.pages?.llm,
    training: n.pages?.training,
    work: n.pages?.work,
    coding: n.pages?.coding,
    gitlab: n.pages?.gitlab,
    agents: n.pages?.agents,
    research: n.pages?.research,
    webex: n.pages?.webex,
    mailboxes: n.pages?.mailboxes,
    conversations: n.pages?.conversations,
    meetings: n.pages?.meetings,
    'company-memory': t.admin.tabCompanyMemory,
    knowledge: t.admin.companyKnowledgeTitle,
    engines: n.pages?.engines,
    usage: n.pages?.usage,
    core: t.admin.core?.title,
    workloads: n.pages?.workloads,
    gateway: t.admin.tabEnvoy,
    platform: t.admin.tabPlatform,
    orchestrator: n.pages?.orchestrator,
    smtm: t.admin.tabSmtm,
    agentic: t.admin.tabVeridion,
    'agentic-rag': t.admin.tabAgenticRag,
    users: t.admin.tabUsers,
    mail: t.admin.tabMail,
    audit: t.admin.tabAudit,
  };
  const adminLayout = [
    { id: 'today', tabs: ['today'] },
    { id: 'organisation', tabs: ['organisation'], flat: 'organisation' },
    { id: 'gametheory', tabs: ['gametheory'] },
    // Every project's newest state as a startable demo, visible as its own place (owner UX, D-VCORE-133).
    // The live previews stand alone; the sovereign LLM has its own group (IDD D-HP-ADMIN-062).
    { id: 'demos', tabs: ['demos'] },
    { id: 'sovereign', tabs: ['llm', 'training'] },
    { id: 'work', tabs: ['work', 'coding', 'gitlab', 'agents', 'research'] },
    { id: 'communication', tabs: ['webex', 'mailboxes', 'conversations', 'meetings'] },
    { id: 'memory', tabs: ['company-memory', 'knowledge'] },
    { id: 'platform', tabs: ['engines', 'usage', 'core', 'workloads', 'gateway', 'platform', 'orchestrator'] },
    { id: 'products', tabs: ['smtm', 'agentic', 'agentic-rag'] },
    { id: 'identity', tabs: ['users', 'mail', 'audit'] },
  ];
  const adminSections = {
    organisation: [['chart', 'colleagues', 'voices', 'people', 'approvals', 'edit']],
    demos: [['previews']],
    llm: [['paper', 'monitoring']],
    training: [['autopilot', 'storage', 'acquisition', 'data', 'training']],
    work: [['items', 'projects']],
    gitlab: [['gitlab', 'builds']],
    agents: [['agents', 'designs', 'definitions']],
    'company-memory': [['day', 'bubbles', 'stores', 'content', 'graph', 'search', 'facts', 'entity'], ['remember', 'audit', 'console']],
    usage: [['identities', 'budgets', 'company', 'smtm']],
    platform: [['nodes', 'events']],
    orchestrator: [['runtime', 'mcp', 'tools', 'activity']],
    smtm: [['runtime', 'prediction'], ['status', 'reporting', 'training', 'console']],
    agentic: [['overview'], ['tools', 'mcp'], ['activity']],
  };
  const sectionLabel = (tab, id) => n.sections?.[tab]?.[id] || id;
  const tabGroups = adminLayout.map((group) => ({
    id: group.id,
    label: n.groups?.[group.id] || group.id,
    tabs: group.tabs.map((id) => ({ id, label: tabLabels[id] })),
    flat: group.flat ? { tab: group.flat, sections: (adminSections[group.flat] || []).flat().map((id) => ({ id, label: sectionLabel(group.flat, id) })) } : null,
  }));
  const activeAdminTab = tabLabels[route.tab] !== undefined ? route.tab : 'today';
  const sectionIds = (adminSections[activeAdminTab] || []).flat();
  const activeSection = sectionIds.includes(route.section)
    ? route.section
    : (lastSections[activeAdminTab] || sectionIds[0] || '');
  const sectionGroups = (adminSections[activeAdminTab] || []).map((group) => group.map((id) => ({ id, label: sectionLabel(activeAdminTab, id) })));
  const selectTab = (tab) => writeAdminRoute(tab, lastSections[tab] || '');
  const renderSmtmFrameMetric = (summary, state) => {
    const frameSummary = summary || {};
    const metricState = state || (frameSummary.exists ? 'available' : 'missing');
    return (
      <div className="admin-smtm-frame-metric">
        <span className={`admin-status-pill ${smtmStateClass(metricState)}`}>{smtmStateLabel(metricState)}</span>
        <span>{t.admin.smtmRows}: {formatInteger(frameSummary.row_count || 0)}</span>
        <span>{t.admin.smtmLatest}: {formatTimestamp(frameSummary.last_timestamp)}</span>
        <span>{t.admin.smtmAge}: {formatMinutes(frameSummary.minutes_since_latest)}</span>
        {frameSummary.latest_value !== null && frameSummary.latest_value !== undefined ? <span>{t.admin.smtmValue}: {String(frameSummary.latest_value)}</span> : null}
      </div>
    );
  };
  const renderSmtmModelMetric = (summary) => {
    const modelSummary = summary || {};
    const metricState = modelSummary.status || 'missing';
    return (
      <div className="admin-smtm-frame-metric">
        <span className={`admin-status-pill ${smtmStateClass(metricState)}`}>{smtmStateLabel(metricState)}</span>
        <span>{t.admin.smtmArtifacts}: {formatInteger(modelSummary.artifact_count || 0)}</span>
        <span>{t.admin.smtmLatestModelUpload}: {formatTimestamp(modelSummary.latest_upload_at)}</span>
      </div>
    );
  };

  const setSectionBusy = (name, value) => {
    setSectionLoading((current) => ({ ...current, [name]: value }));
  };

  const loadMailSettingsSection = async () => {
    setSectionBusy('mailSettings', true);
    try {
      const payload = await requestJson('/api/admin/mail-settings');
      setMailSettings((current) => ({ ...current, ...payload, smtp_password: '' }));
    } catch (requestError) {
      setError(requestError.code || 'UNKNOWN_ERROR');
    } finally {
      setSectionBusy('mailSettings', false);
    }
  };

  const loadUsersSection = async () => {
    setSectionBusy('users', true);
    try {
      const payload = await requestJson('/api/admin/users');
      setUsers(payload.users || []);
      setAvailableRoles(payload.available_roles?.length ? payload.available_roles : fallbackRoles);
      setAvailableStatuses(payload.available_statuses?.length ? payload.available_statuses : fallbackStatuses);
      setUserActions(userActionTemplate);
    } catch (requestError) {
      setError(requestError.code || 'UNKNOWN_ERROR');
    } finally {
      setSectionBusy('users', false);
    }
  };

  const loadAuditSection = async () => {
    setSectionBusy('audit', true);
    try {
      const payload = await requestJson('/api/admin/audit');
      setAudit(payload.events || []);
    } catch (requestError) {
      setError(requestError.code || 'UNKNOWN_ERROR');
    } finally {
      setSectionBusy('audit', false);
    }
  };

  const loadSessionSummarySection = async () => {
    setSectionBusy('sessionSummary', true);
    try {
      const payload = await requestJson('/api/admin/session-summary');
      setSessionSummary({ ...sessionSummaryTemplate, ...payload });
    } catch (requestError) {
      setError(requestError.code || 'UNKNOWN_ERROR');
    } finally {
      setSectionBusy('sessionSummary', false);
    }
  };

  const loadAiUsageAndGatewaySection = async () => {
    setSectionBusy('aiUsage', true);
    try {
      setAiUsage({ ...aiUsageTemplate, ...(await requestJson('/api/admin/ai-usage')) });
    } catch (usageError) {
      setAiUsage({ ...aiUsageTemplate, error: usageError.code || 'AI_USAGE_UNAVAILABLE' });
    } finally {
      setSectionBusy('aiUsage', false);
    }

    try {
      setGpu({ data: await requestJson('/api/admin/gpu'), error: '' });
    } catch (gpuError) {
      setGpu({ data: null, error: gpuError.code || 'GPU_UNAVAILABLE' });
    }
    setSectionBusy('aiGateway', true);
    try {
      setAiGateway({ ...aiGatewayTemplate, ...(await requestJson('/api/admin/ai-gateway')) });
    } catch (gatewayError) {
      setAiGateway({ ...aiGatewayTemplate, error: gatewayError.code || 'AI_GATEWAY_UNAVAILABLE' });
    } finally {
      setSectionBusy('aiGateway', false);
    }
  };

  const loadPlatformSection = async () => {
    setSectionBusy('platform', true);
    try {
      setPlatform({ ...platformTemplate, ...(await requestJson('/api/admin/platform')) });
    } catch (platformError) {
      setPlatform({ ...platformTemplate, error: platformError.code || 'PLATFORM_UNAVAILABLE' });
    } finally {
      setSectionBusy('platform', false);
    }
  };

  const loadServicesSection = async () => {
    setSectionBusy('services', true);
    try {
      const servicesPayload = await requestJson('/api/admin/services');
      setServices({ error: '', services: servicesPayload.services || [] });
    } catch (servicesError) {
      setServices({ error: servicesError.code || 'ADMIN_SERVICES_UNAVAILABLE', services: [] });
    } finally {
      setSectionBusy('services', false);
    }
  };

  const loadSmtmStatusSection = async (filterValue = smtmInstrumentFilter) => {
    setSectionBusy('smtmStatus', true);
    try {
      const smtmPayload = await requestJson(`/api/admin/smtm-status${buildSmtmFilterQuery(filterValue)}`);
      startTransition(() => {
        setSmtmStatus({ ...smtmStatusTemplate, ...smtmPayload, error: '' });
      });
    } catch (smtmError) {
      startTransition(() => {
        setSmtmStatus({ ...smtmStatusTemplate, error: smtmError.code || 'SMTM_STATUS_UNAVAILABLE' });
      });
    } finally {
      setSectionBusy('smtmStatus', false);
    }
  };

  const loadSmtmPredictionRuntimeSection = async () => {
    setSectionBusy('smtmPredictionRuntime', true);
    try {
      const payload = await requestJson('/api/admin/smtm-prediction-runtime');
      const runtime = { ...smtmPredictionRuntimeTemplate.runtime, ...(payload?.runtime || {}) };
      startTransition(() => {
        setSmtmPredictionRuntime({ ...smtmPredictionRuntimeTemplate, ...payload, runtime, error: '' });
        setSmtmPredictionRuntimeDraft(runtime);
      });
    } catch (runtimeError) {
      startTransition(() => {
        setSmtmPredictionRuntime({ ...smtmPredictionRuntimeTemplate, error: runtimeError.code || 'SMTM_PREDICTION_RUNTIME_UNAVAILABLE' });
      });
    } finally {
      setSectionBusy('smtmPredictionRuntime', false);
    }
  };

  const loadSmtmTrainingConsoleSection = async () => {
    setSectionBusy('smtmTrainingConsole', true);
    try {
      const payload = await requestJson('/api/admin/smtm-training-console');
      startTransition(() => {
        setSmtmTrainingConsole({ ...smtmTrainingConsoleTemplate, ...payload, error: '' });
      });
    } catch (consoleError) {
      startTransition(() => {
        setSmtmTrainingConsole({ ...smtmTrainingConsoleTemplate, error: consoleError.code || 'SMTM_TRAINING_CONSOLE_UNAVAILABLE' });
      });
    } finally {
      setSectionBusy('smtmTrainingConsole', false);
    }
  };

  const loadSmtmPredictionSettingsSection = async (filterValue = smtmInstrumentFilter) => {
    setSectionBusy('smtmPredictionSettings', true);
    try {
      const payload = await requestJson(`/api/admin/smtm-prediction-settings${buildSmtmFilterQuery(filterValue)}`);
      startTransition(() => {
        setSmtmPredictionSettings({ ...smtmPredictionSettingsTemplate, ...payload, error: '' });
        setSmtmPredictionDrafts({});
      });
    } catch (settingsError) {
      startTransition(() => {
        setSmtmPredictionSettings({ ...smtmPredictionSettingsTemplate, error: settingsError.code || 'SMTM_PREDICTION_SETTINGS_UNAVAILABLE' });
      });
    } finally {
      setSectionBusy('smtmPredictionSettings', false);
    }
  };

  const loadSmtmReportingSection = async (filterValue = smtmInstrumentFilter) => {
    setSectionBusy('smtmReporting', true);
    try {
      const payload = await requestJson(`/api/admin/smtm-reporting${buildSmtmFilterQuery(filterValue)}`);
      startTransition(() => {
        setSmtmReporting({ ...smtmReportingTemplate, ...payload, error: '' });
      });
    } catch (reportingError) {
      startTransition(() => {
        setSmtmReporting({ ...smtmReportingTemplate, error: reportingError.code || 'SMTM_REPORTING_UNAVAILABLE' });
      });
    } finally {
      setSectionBusy('smtmReporting', false);
    }
  };

  const loadSmtmTrainingSection = async (filterValue = smtmInstrumentFilter) => {
    setSectionBusy('smtmTraining', true);
    try {
      const payload = await requestJson(`/api/admin/smtm-training${buildSmtmFilterQuery(filterValue)}`);
      startTransition(() => {
        setSmtmTraining({ ...smtmTrainingTemplate, ...payload, error: '' });
      });
    } catch (trainingError) {
      startTransition(() => {
        setSmtmTraining({ ...smtmTrainingTemplate, error: trainingError.code || 'SMTM_TRAINING_UNAVAILABLE' });
      });
    } finally {
      setSectionBusy('smtmTraining', false);
    }
  };

  const refreshSmtmSections = (filterValue = smtmInstrumentFilter) => {
    loadSmtmPredictionRuntimeSection();
    loadSmtmTrainingConsoleSection();
    loadSmtmPredictionSettingsSection(filterValue);
    loadSmtmStatusSection(filterValue);
    loadSmtmReportingSection(filterValue);
    loadSmtmTrainingSection(filterValue);
  };

  const stageSmtmPredictionSetting = (baseName, enabled) => {
    setSmtmPredictionDrafts((current) => ({
      ...current,
      [baseName]: enabled,
    }));
  };

  const resetSmtmPredictionDrafts = () => {
    setSmtmPredictionDrafts({});
  };

  const updateSmtmPredictionRuntimeDraft = (field, value) => {
    setSmtmPredictionRuntimeDraft((current) => ({ ...current, [field]: value }));
  };

  const resetSmtmPredictionRuntimeDraft = () => {
    setSmtmPredictionRuntimeDraft({ ...(smtmPredictionRuntime.runtime || smtmPredictionRuntimeTemplate.runtime) });
  };

  const saveSmtmPredictionRuntime = async () => {
    setMessage('');
    setError('');
    setSmtmPredictionRuntimeSaving(true);
    try {
      const payload = await requestJson('/api/admin/smtm-prediction-runtime', {
        method: 'POST',
        body: JSON.stringify({
          forecast_horizon_minutes: Number(smtmPredictionRuntimeDraft.forecast_horizon_minutes || 30),
          resolution_minutes: Number(smtmPredictionRuntimeDraft.resolution_minutes || 30),
          require_completed_bars: Boolean(smtmPredictionRuntimeDraft.require_completed_bars),
          prediction_gate_open: Boolean(smtmPredictionRuntimeDraft.prediction_gate_open),
          rebuild_active: Boolean(smtmPredictionRuntimeDraft.rebuild_active),
        }),
      });
      const runtime = { ...smtmPredictionRuntimeTemplate.runtime, ...(payload?.runtime || {}) };
      setSmtmPredictionRuntime({ ...smtmPredictionRuntimeTemplate, ...payload, runtime, error: '' });
      setSmtmPredictionRuntimeDraft(runtime);
      setMessage(t.admin.smtmRuntimeSaved);
      loadSmtmStatusSection(smtmInstrumentFilter);
    } catch (requestError) {
      setError(requestError.code || 'SMTM_PREDICTION_RUNTIME_SAVE_FAILED');
    } finally {
      setSmtmPredictionRuntimeSaving(false);
    }
  };

  const saveSmtmPredictionSettings = async () => {
    const updates = (smtmPredictionSettings.instruments || [])
      .map((entry) => {
        const draftValue = smtmPredictionDrafts[entry.base_name];
        if (draftValue === undefined || draftValue === Boolean(entry.prediction_enabled)) {
          return null;
        }
        return {
          base_name: entry.base_name,
          enabled: Boolean(draftValue),
        };
      })
      .filter(Boolean);

    if (!updates.length) {
      return;
    }

    setMessage('');
    setError('');
    setSmtmPredictionSaving(true);
    try {
      const payload = await requestJson('/api/admin/smtm-prediction-settings', {
        method: 'POST',
        body: JSON.stringify({ updates }),
      });
      const updatedEntries = Array.isArray(payload?.updated) ? payload.updated : [];
      if (updatedEntries.length) {
        const updatedByName = Object.fromEntries(updatedEntries.map((entry) => [entry.base_name, entry]));
        setSmtmPredictionSettings((current) => ({
          ...current,
          instruments: (current.instruments || []).map((entry) => (
            updatedByName[entry.base_name]
              ? { ...entry, ...updatedByName[entry.base_name] }
              : entry
          )),
          summary: {
            ...(current.summary || {}),
            prediction_enabled: (current.instruments || []).reduce((count, entry) => {
              const nextEntry = updatedByName[entry.base_name];
              if (nextEntry) {
                return count + (nextEntry.prediction_enabled ? 1 : 0);
              }
              return count + (entry.prediction_enabled ? 1 : 0);
            }, 0),
            prediction_disabled: (current.instruments || []).reduce((count, entry) => {
              const nextEntry = updatedByName[entry.base_name];
              if (nextEntry) {
                return count + (nextEntry.prediction_enabled ? 0 : 1);
              }
              return count + (entry.prediction_enabled ? 0 : 1);
            }, 0),
          },
        }));
      }
      setSmtmPredictionDrafts({});
      setMessage(t.admin.smtmPredictionSettingsSaved);
      loadSmtmPredictionSettingsSection(smtmInstrumentFilter);
      loadSmtmStatusSection(smtmInstrumentFilter);
    } catch (requestError) {
      setError(requestError.code || 'SMTM_PREDICTION_SETTINGS_SAVE_FAILED');
    } finally {
      setSmtmPredictionSaving(false);
    }
  };

  // Writes into the live BackendTrafficPolicy, which owns the budgets.
  const applyBudget = async (body) => {
    setMessage('');
    setError('');
    setBudgetSaving(true);
    try {
      setAiGateway({ ...aiGatewayTemplate, ...(await requestJson('/api/admin/ai-budget', {
        method: 'POST',
        body: JSON.stringify(body),
      })) });
      setBudgetDraft({ default: '', identity: '', tokens: '' });
      setMessage(t.admin.aiBudgetSaved);
      try { setGpu({ data: await requestJson('/api/admin/gpu'), error: '' }); } catch (gpuError) { setGpu({ data: null, error: gpuError.code || 'GPU_UNAVAILABLE' }); }
    } catch (budgetError) {
      setError(budgetError.code || 'AI_BUDGET_SAVE_FAILED');
    } finally {
      setBudgetSaving(false);
    }
  };

  const resetIdentityWindow = async (identity) => {
    setMessage('');
    setError('');
    try {
      const payload = await requestJson('/api/admin/ai-usage-reset', {
        method: 'POST',
        body: JSON.stringify({ identity }),
      });
      setAiUsage({ ...aiUsageTemplate, ...(payload.usage || {}) });
      setMessage(t.admin.aiUsageResetDone);
    } catch (resetError) {
      setError(resetError.code || 'AI_USAGE_RESET_FAILED');
    }
  };

  const getUserActionDefaults = (userId) => {
    const user = users.find((candidate) => candidate.id === userId);
    return {
      roles: normaliseRoleList(user?.roles, availableRoles),
      status: availableStatuses.includes(user?.status) ? user.status : availableStatuses[0],
      password: '',
    };
  };

  const load = async () => {
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const currentUser = await fetchCurrentUser();
      setMe(currentUser.user);
      if (!currentUser.user?.roles?.includes('admin')) {
        setLoading(false);
        return;
      }

      setSectionLoading(sectionLoadingTemplate);
      setLoading(false);
      loadMailSettingsSection();
      loadUsersSection();
      loadAuditSection();
      loadSessionSummarySection();
      loadAiUsageAndGatewaySection();
      loadPlatformSection();
      loadServicesSection();
      refreshSmtmSections();
    } catch (requestError) {
      setError(requestError.code || 'ADMIN_REQUIRED');
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const follow = () => setRoute(currentRoute());
    window.addEventListener('hashchange', follow);
    return () => window.removeEventListener('hashchange', follow);
  }, []);

  useEffect(() => {
    if (activeSection) setLastSections((current) => (current[activeAdminTab] === activeSection ? current : { ...current, [activeAdminTab]: activeSection }));
    if (window.scrollY > 0) window.scrollTo({ top: 0 });
  }, [activeAdminTab, activeSection]);

  // The administrator's last look at the day: stamped when the first page is left and when the tab closes, so the
  // next visit shows what happened since.
  useEffect(() => {
    if (activeAdminTab !== 'today') return undefined;
    const stamp = () => writeAdminStored('lastLook', new Date().toISOString());
    window.addEventListener('beforeunload', stamp);
    return () => { window.removeEventListener('beforeunload', stamp); stamp(); };
  }, [activeAdminTab]);

  // What needs a person is read every minute from the runtimes that own it: the platform, the company
  // instance with Veridion Core, the organisation's approvals, the coding tasks and the MCP servers of
  // Veridion SMTM. The overview lists it; the tabs and sections count it.
  const loadAttentionSection = async () => {
    const dayAgo = new Date(Date.now() - 24 * 3600 * 1000).toISOString();
    const [company, organisation, tasks, smtmMcp, timeline, workItems, meetings, word] = await Promise.allSettled([
      requestJson('/api/admin/veridion-paioneer/overview'),
      requestJson('/api/admin/veridion-core/organisation'),
      requestJson('/api/admin/veridion-core/tasks?only_open=true'),
      requestJson('/api/admin/veridion-smtm/mcp-servers'),
      requestJson(`/api/admin/veridion-core/timeline?since=${encodeURIComponent(today.since)}&limit=200`),
      requestJson('/api/admin/veridion-core/work-items'),
      requestJson('/api/admin/veridion-core/meetings?limit=20'),
      // The standing word is the newest word of the last day, whatever the last look was (D-HP-ADMIN-040).
      requestJson(`/api/admin/veridion-core/timeline?since=${encodeURIComponent(dayAgo)}&kind=word&limit=1`),
    ]);
    const value = (result) => (result.status === 'fulfilled' ? result.value : null);
    setAttention({ loaded: true, company: value(company), organisation: value(organisation), tasks: value(tasks), smtmMcp: value(smtmMcp) });
    setToday((current) => ({
      ...current,
      loaded: true,
      error: timeline.status === 'fulfilled' ? '' : (timeline.reason?.code || 'TIMELINE_UNAVAILABLE'),
      timeline: value(timeline)?.items || [],
      workItems: value(workItems)?.items || [],
      meetings: value(meetings)?.items || [],
      word: (value(word)?.items || [])[0] || null,
    }));
  };

  // A meeting held now, in the administrator's name; the Core says when a meeting runs or just ended.
  const holdMeeting = async () => {
    setMeetingBusy(true);
    setMeetingNotice('');
    try {
      const result = await requestJson('/api/admin/veridion-core/meetings/hold?kind=hourly', { method: 'POST', body: JSON.stringify({}) });
      setMeetingNotice(result?.held === false ? (result.message || t.admin.nav?.meetings?.notHeld) : t.admin.nav?.meetings?.held);
      loadAttentionSection();
    } catch (failure) {
      setMeetingNotice(`${t.admin.nav?.meetings?.holdFailed} (${failure?.code || failure?.status || '-'})`);
    } finally {
      setMeetingBusy(false);
    }
  };

  useEffect(() => {
    if (!me?.roles?.includes('admin')) return undefined;
    loadAttentionSection();
    const timer = window.setInterval(() => { loadAttentionSection(); loadPlatformSection(); }, 60000);
    return () => window.clearInterval(timer);
  }, [me]);

  const filteredUsers = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    return users.filter((user) => {
      if (needle && !`${user.email} ${user.username} ${user.roles.join(' ')}`.toLowerCase().includes(needle)) return false;
      if (userStatusFilter && user.status !== userStatusFilter) return false;
      if (userRoleFilter && !user.roles.includes(userRoleFilter)) return false;
      return true;
    });
  }, [filter, userStatusFilter, userRoleFilter, users]);

  // A page of rows, so the list stays readable when the account count grows.
  const USERS_PER_PAGE = 25;
  const userPageCount = Math.max(1, Math.ceil(filteredUsers.length / USERS_PER_PAGE));
  const currentUserPage = Math.min(userPage, userPageCount - 1);
  const pagedUsers = filteredUsers.slice(
    currentUserPage * USERS_PER_PAGE,
    currentUserPage * USERS_PER_PAGE + USERS_PER_PAGE,
  );
  const selectedUser = users.find((user) => user.id === selectedUserId) || null;
  const userStatusClass = (status) => (
    status === 'active' ? 'is-ok' : status === 'disabled' ? 'is-missing' : 'is-warning');
  // Date and time to the minute: the column is for orientation, not for forensics.
  const formatDay = (value) => formatBerlin(value, { seconds: false });

  const selectedSmtmTrainingModel = useMemo(() => {
    if (!Array.isArray(smtmTraining.models) || !smtmTraining.models.length) {
      return null;
    }
    return smtmTraining.models.find((model) => model.model_name === selectedSmtmTrainingModelName) || smtmTraining.models[0];
  }, [selectedSmtmTrainingModelName, smtmTraining.models]);

  useEffect(() => {
    if (!Array.isArray(smtmTraining.models) || !smtmTraining.models.length) {
      if (selectedSmtmTrainingModelName) {
        setSelectedSmtmTrainingModelName('');
      }
      return;
    }
    if (!selectedSmtmTrainingModelName || !smtmTraining.models.some((model) => model.model_name === selectedSmtmTrainingModelName)) {
      setSelectedSmtmTrainingModelName(smtmTraining.models[0].model_name);
    }
  }, [selectedSmtmTrainingModelName, smtmTraining.models]);

  const pendingSmtmPredictionRuntimeChanges = useMemo(() => {
    const current = smtmPredictionRuntime.runtime || smtmPredictionRuntimeTemplate.runtime;
    return JSON.stringify(current) !== JSON.stringify(smtmPredictionRuntimeDraft || {});
  }, [smtmPredictionRuntime.runtime, smtmPredictionRuntimeDraft]);

  const pendingSmtmPredictionChanges = useMemo(() => (
    (smtmPredictionSettings.instruments || []).reduce((count, entry) => {
      const draftValue = smtmPredictionDrafts[entry.base_name];
      if (draftValue === undefined || draftValue === Boolean(entry.prediction_enabled)) {
        return count;
      }
      return count + 1;
    }, 0)
  ), [smtmPredictionDrafts, smtmPredictionSettings.instruments]);

  const attentionItems = useMemo(() => {
    const items = [];
    const add = (tab, section, tone, text) => items.push({ tab, section, tone, text });
    if (platform.error) add('workloads', '', 'error', a.platformUnreadable);
    (platform.nodes || []).forEach((node) => {
      if (!node.ready) add('platform', 'nodes', 'error', fill(a.nodeNotReady, { name: node.name }));
      else if (node.pressure?.length) add('platform', 'nodes', 'warning', fill(a.nodePressure, { name: node.name, pressure: node.pressure.join(', ') }));
    });
    (platform.namespaces || []).forEach((entry) => (entry.workloads || []).forEach((workload) => {
      if (workload.state && workload.state !== 'ready') {
        add('workloads', '', workload.state === 'degraded' ? 'warning' : 'error',
          fill(a.workloadDown, { name: workload.name, namespace: entry.namespace, ready: workload.ready, desired: workload.desired }));
      }
    }));
    if (!sectionLoading.aiGateway && !aiGateway.gateway?.programmed) add('gateway', '', 'error', a.gatewayNotProgrammed);

    if (smtmPredictionRuntime.generated_at && !smtmPredictionRuntime.runtime?.prediction_gate_open) add('smtm', 'runtime', 'warning', a.smtmGateClosed);
    if (smtmPredictionRuntime.generated_at && smtmPredictionRuntime.runtime?.rebuild_active) add('smtm', 'runtime', 'warning', a.smtmRebuild);
    (smtmStatus.runtime?.hints || []).forEach((hint) => add('smtm', 'status', 'warning', fill(a.smtmHint, { hint: smtmHintLabel(hint) })));
    if (Number(smtmReporting.summary?.delivery_failed) > 0) add('smtm', 'reporting', 'warning', fill(a.smtmDeliveryFailed, { count: formatInteger(smtmReporting.summary.delivery_failed) }));
    if (Number(smtmTraining.summary?.failed_models) > 0) add('smtm', 'training', 'warning', fill(a.smtmModelsFailed, { count: formatInteger(smtmTraining.summary.failed_models) }));

    ((attention.smtmMcp?.items) || []).filter((server) => server.status === 'unreachable').forEach((server) => (
      add('agentic', 'mcp', 'error', fill(a.mcpUnreachable, { server: server.displayName || server.serverId, instance: t.admin.tabVeridion }))));

    const company = attention.company;
    if (company) {
      const parts = { health: 'orchestrator', webex: 'webex', knowledge: 'knowledge', core: 'core', mcp_servers: 'mcp', usage: 'usage', gateway: 'gateway' };
      Object.entries(parts).forEach(([part, label]) => {
        if (company[part]?.error) add(({ core: 'core', webex: 'webex', knowledge: 'knowledge', mcp: 'orchestrator', orchestrator: 'orchestrator', usage: 'usage', gateway: 'usage' })[label] || 'core', label === 'mcp' ? 'mcp' : label === 'usage' || label === 'gateway' ? 'company' : '', 'error', fill(a.partUnavailable, { part: a.parts?.[label] || label }));
      });
      const core = company.core?.data;
      if (core?.core?.emergencyStop) add('core', '', 'error', a.emergencyStop);
      if (core?.core?.missing?.length) add('core', '', 'error', fill(a.coreMisconfigured, { missing: core.core.missing.join(', ') }));
      if (Number(core?.agents?.waiting) > 0) add('agents', 'agents', 'warning', fill(a.agentsWaiting, { count: core.agents.waiting }));
      (core?.hosted || []).filter((entry) => ['starting', 'not_deployed'].includes(entry.status)).forEach((entry) => (
        add('agents', 'agents', 'error', fill(a.hostedNotRunning, { agent: entry.title }))));
      const webex = company.webex?.data;
      (webex?.bots || []).filter((bot) => !bot.webhook?.registered).forEach((bot) => (
        add('webex', '', 'warning', fill(a.webhookMissing, { bot: bot.displayName || bot.key }))));
      if (webex?.reader?.status?.configured && !webex.reader.status.authorised) add('organisation', 'people', 'warning', a.readerNotAuthorised);
      ((company.mcp_servers?.data?.items) || []).filter((server) => server.status === 'unreachable').forEach((server) => (
        add('orchestrator', 'mcp', 'error', fill(a.mcpUnreachable, { server: server.displayName || server.serverId, instance: t.admin.companyTitle }))));
    }
    // On autopilot there is no human decision (IDD D-VCORE-295): open cards, waiting tasks and reviews are the
    // autopilot's lane and never a person's attention; only real platform alarms stay visible.
    const autopilotOn = Boolean(attention.company?.core?.data?.core?.autopilot);
    if (!autopilotOn) {
      ((attention.organisation?.approvals) || []).forEach((approval) => (
        add('organisation', 'approvals', 'warning', fill(a.approvalOpen, {
          position: (attention.organisation.positions || []).find((position) => position.key === approval.position)?.bot || approval.position,
          question: approval.question,
        }))));
      ((attention.tasks?.items) || []).forEach((task) => {
        if (['waiting', 'needs_person'].includes(task.status)) add('coding', '', 'warning', fill(a.taskWaiting, { title: task.title }));
        if (task.status === 'review') add('coding', '', 'warning', fill(a.taskReview, { title: task.title }));
      });
    }
    return items;
  }, [t, platform, aiGateway, sectionLoading.aiGateway, smtmPredictionRuntime, smtmStatus, smtmReporting, smtmTraining, attention]);

  // A page's badge counts its items; a section's badge counts the items that lead into it. The flat group reads a
  // section's badge under `page/section`, whatever page is open.
  const tabBadges = {};
  const sectionBadges = {};
  attentionItems.forEach((item) => {
    const tabBadge = tabBadges[item.tab] || (tabBadges[item.tab] = { count: 0, tone: 'warning' });
    tabBadge.count += 1;
    if (item.tone === 'error') tabBadge.tone = 'error';
    if (item.section) {
      const flat = tabBadges[`${item.tab}/${item.section}`] || (tabBadges[`${item.tab}/${item.section}`] = { count: 0, tone: 'warning' });
      flat.count += 1;
      if (item.tone === 'error') flat.tone = 'error';
    }
    if (item.tab !== activeAdminTab) return;
    const badge = sectionBadges[item.section] || (sectionBadges[item.section] = { count: 0, tone: 'warning' });
    badge.count += 1;
    if (item.tone === 'error') badge.tone = 'error';
  });

  const updateUserAction = (userId, field, value) => {
    setUserActions((current) => ({
      ...current,
      [userId]: {
        ...getUserActionDefaults(userId),
        ...current[userId],
        roles: normaliseRoleList(current[userId]?.roles ?? getUserActionDefaults(userId).roles, availableRoles),
        password: current[userId]?.password ?? '',
        [field]: value,
      },
    }));
  };

  const saveMailSettings = async (event) => {
    event.preventDefault();
    setMessage('');
    setError('');
    try {
      const payload = await requestForm('/api/admin/mail-settings', mailSettings);
      setMailSettings((current) => ({ ...current, ...payload, smtp_password: '' }));
      setMessage(t.admin.mailSettingsSaved);
    } catch (requestError) {
      setError(requestError.code || 'UNKNOWN_ERROR');
    }
  };

  const sendTestMail = async () => {
    setMessage('');
    setError('');
    try {
      await requestForm('/api/admin/mail-settings/test', { recipient: testRecipient });
      setMessage(t.admin.mailTestSent);
      await load();
    } catch (requestError) {
      setError(requestError.code || 'UNKNOWN_ERROR');
    }
  };

  const handleCreateUser = async (event) => {
    event.preventDefault();
    setMessage('');
    setError('');
    if ((createUser.password || '').length < 12) {
      setError('PASSWORD_TOO_WEAK');
      return;
    }
    try {
      await requestForm('/api/admin/users', { ...createUser, roles: serialiseRoles(createUser.roles, availableRoles) });
      setCreateUser(createUserTemplate);
      setMessage(t.admin.userCreatedSuccess);
      await load();
    } catch (requestError) {
      setError(requestError.code || 'UNKNOWN_ERROR');
    }
  };

  const runUserAction = async (userId, type) => {
    const current = userActions[userId] || getUserActionDefaults(userId);
    const targetUser = users.find((candidate) => candidate.id === userId);
    setMessage('');
    setError('');
    if (type === 'password' && (current.password || '').length < 12) {
      setError('PASSWORD_TOO_WEAK');
      return;
    }
    if (type === 'delete') {
      const confirmed = window.confirm(`${t.admin.deleteUser}: ${targetUser?.email || userId}?`);
      if (!confirmed) {
        return;
      }
    }
    try {
      if (type === 'password') {
        await requestForm(`/api/admin/users/${userId}/password`, { password: current.password });
      }
      if (type === 'roles') {
        await requestForm(`/api/admin/users/${userId}/roles`, { roles: serialiseRoles(current.roles, availableRoles) });
      }
      if (type === 'status') {
        await requestForm(`/api/admin/users/${userId}/status`, { status: current.status });
      }
      if (type === 'mfa') {
        await requestForm(`/api/admin/users/${userId}/mfa-reset`, {});
      }
      if (type === 'sessions') {
        await requestForm(`/api/admin/users/${userId}/sessions/revoke`, {});
      }
      if (type === 'delete') {
        await requestForm(`/api/admin/users/${userId}/delete`, {});
      }
      setMessage(type === 'delete' ? t.admin.userDeletedSuccess : t.admin.userUpdatedSuccess);
      await load();
    } catch (requestError) {
      setError(requestError.code || 'UNKNOWN_ERROR');
    }
  };

  const renderRoleCheckboxes = (roles, onChange) => {
    const selectedRoles = normaliseRoleList(roles, availableRoles);
    return (
      <div className="flex flex-wrap gap-3">
        {availableRoles.map((role) => (
          <label key={role} className="homepage-muted-panel px-3 py-2 flex items-center gap-2 text-sm text-text-secondary">
            <input
              type="checkbox"
              checked={selectedRoles.includes(role)}
              disabled={role === 'user'}
              onChange={(event) => onChange(toggleRole(selectedRoles, role, event.target.checked, availableRoles))}
            />
            <span>{t.admin.roleLabels?.[role] || role}</span>
          </label>
        ))}
      </div>
    );
  };

  if (loading) {
    return <main className="site-main"><div className="site-card p-6">{t.auth.validating}</div></main>;
  }

  if (!me || !me.roles.includes('admin')) {
    return <main className="site-main"><div className="site-card p-6">{t.auth.errors.adminRequired}</div></main>;
  }

  const smtmFiltered = ['prediction', 'status', 'reporting', 'training'].includes(activeSection);
  const labelsForBlocks = { expand: n.expand, collapse: n.collapse, showMore: n.showMore, shownOf: n.shownOf, close: t.admin.usersClose };
  const pageTitle = tabLabels[activeAdminTab] || '';
  const subtitleOf = { today: formatBerlin(new Date(), { seconds: false }), llm: t.admin.llmPaper?.subtitle }[activeAdminTab] || '';
  const readyOf = (ready, total) => fill(n.readyOf, { ready: formatInteger(ready), total: formatInteger(total) });
  const stateRow = (key, label, text, tab, section = '', tone = 'ok') => (
    <button key={key} type="button" className="admin-attention-item" onClick={() => writeAdminRoute(tab, section)}>
      <span className={`admin-status-pill ${tone === 'error' ? 'is-missing' : tone === 'warning' ? 'is-warning' : 'is-ok'}`}>{label}</span>
      <span className="admin-attention-text">{text}</span>
      <span className="admin-attention-where">{tabLabels[tab]}{section && n.sections?.[tab]?.[section] ? ` · ${n.sections[tab][section]}` : ''}</span>
    </button>
  );
  const smtmSections = (
    <>
      {smtmFiltered ? (
        <div className="admin-toolbar" role="search" aria-label={t.admin.smtmFilterTitle}>
          <input
            className="form-input"
            placeholder={t.admin.smtmFilterPlaceholder}
            title={t.admin.smtmFilterSubtitle}
            value={smtmInstrumentFilter}
            onChange={(event) => setSmtmInstrumentFilter(event.target.value)}
          />
          <button type="button" className="homepage-button-primary" onClick={() => refreshSmtmSections(smtmInstrumentFilter)}>{t.admin.smtmFilterApply}</button>
          <button type="button" className="homepage-button-secondary" onClick={() => { setSmtmInstrumentFilter(''); refreshSmtmSections(''); }}>{t.admin.smtmFilterClear}</button>
        </div>
      ) : null}
      {activeSection === 'runtime' ? (
        <AdminSection id="smtm-runtime" title={t.admin.smtmRuntimeTitle} subtitle={t.admin.smtmRuntimeSubtitle}
          state={smtmPredictionRuntime.generated_at ? `${t.admin.smtmRuntimePredictionGate}: ${smtmPredictionRuntime.runtime?.prediction_gate_open ? t.admin.smtmRuntimeStateOpen : t.admin.smtmRuntimeStateClosed} · ${t.admin.smtmRuntimeRebuild}: ${smtmPredictionRuntime.runtime?.rebuild_active ? t.admin.smtmRuntimeStateActive : t.admin.smtmRuntimeStateIdle}` : ''}
          tone={smtmPredictionRuntime.error ? 'error' : smtmPredictionRuntime.runtime?.prediction_gate_open && !smtmPredictionRuntime.runtime?.rebuild_active ? 'ok' : 'warning'}>
          {sectionLoading.smtmPredictionRuntime ? <div className="homepage-muted-panel p-4 text-sm text-text-secondary">{t.auth.validating}</div> : null}
          {smtmPredictionRuntime.error ? <div className="homepage-muted-panel p-4 text-sm text-error">{t.admin.smtmRuntimeUnavailable}: {smtmPredictionRuntime.error}</div> : null}
          <FactGrid items={[
            [t.admin.smtmGeneratedAt, formatTimestamp(smtmPredictionRuntime.generated_at)],
            [t.admin.smtmRuntimeConfigVersion, String(smtmPredictionRuntime.runtime?.config_version ?? '-')],
          ]} />
          <div className="admin-form-row">
            <label className="admin-field">{t.admin.smtmRuntimeForecastHorizonMinutes}
              <input className="form-input" type="number" min="30" step="30" value={smtmPredictionRuntimeDraft.forecast_horizon_minutes ?? 30} onChange={(event) => updateSmtmPredictionRuntimeDraft('forecast_horizon_minutes', event.target.value)} />
            </label>
            <label className="admin-field">{t.admin.smtmRuntimeResolutionMinutes}
              <input className="form-input" type="number" min="1" step="1" value={smtmPredictionRuntimeDraft.resolution_minutes ?? 30} onChange={(event) => updateSmtmPredictionRuntimeDraft('resolution_minutes', event.target.value)} />
            </label>
          </div>
          <div className="flex flex-wrap gap-4">
            <label className="admin-check">
              <input type="checkbox" checked={Boolean(smtmPredictionRuntimeDraft.require_completed_bars)} onChange={(event) => updateSmtmPredictionRuntimeDraft('require_completed_bars', event.target.checked)} />
              <span>{t.admin.smtmRuntimeRequireCompletedBars}</span>
            </label>
            <label className="admin-check">
              <input type="checkbox" checked={Boolean(smtmPredictionRuntimeDraft.prediction_gate_open)} onChange={(event) => updateSmtmPredictionRuntimeDraft('prediction_gate_open', event.target.checked)} />
              <span>{t.admin.smtmRuntimePredictionGateOpen}</span>
            </label>
            <label className="admin-check">
              <input type="checkbox" checked={Boolean(smtmPredictionRuntimeDraft.rebuild_active)} onChange={(event) => updateSmtmPredictionRuntimeDraft('rebuild_active', event.target.checked)} />
              <span>{t.admin.smtmRuntimeRebuildActive}</span>
            </label>
          </div>
          <div className="admin-inline-actions">
            <button type="button" className="homepage-button-primary" disabled={!pendingSmtmPredictionRuntimeChanges || smtmPredictionRuntimeSaving} onClick={saveSmtmPredictionRuntime}>{smtmPredictionRuntimeSaving ? t.admin.smtmRuntimeSaving : t.admin.smtmRuntimeSave}</button>
            <button type="button" className="homepage-button-secondary" disabled={!pendingSmtmPredictionRuntimeChanges || smtmPredictionRuntimeSaving} onClick={resetSmtmPredictionRuntimeDraft}>{t.admin.smtmRuntimeReset}</button>
          </div>
        </AdminSection>
      ) : null}
      {activeSection === 'prediction' ? (
        <AdminSection id="smtm-prediction" title={t.admin.smtmPredictionControlTitle} subtitle={t.admin.smtmPredictionControlSubtitle}
          count={smtmPredictionSettings.summary?.tracked_instruments || 0}
          state={`${t.admin.smtmPredictionEnabledCount}: ${formatInteger(smtmPredictionSettings.summary?.prediction_enabled || 0)} · ${t.admin.smtmPredictionDisabledCount}: ${formatInteger(smtmPredictionSettings.summary?.prediction_disabled || 0)}`}
          actions={<span className="text-sm text-text-secondary">{t.admin.smtmGeneratedAt}: {sectionLoading.smtmPredictionSettings ? '...' : formatTimestamp(smtmPredictionSettings.generated_at)}</span>}>
          {sectionLoading.smtmPredictionSettings ? <div className="homepage-muted-panel p-4 text-sm text-text-secondary">{t.auth.validating}</div> : null}
          {smtmPredictionSettings.error ? <div className="homepage-muted-panel p-4 text-sm text-error">{t.admin.smtmPredictionSettingsUnavailable}: {smtmPredictionSettings.error}</div> : null}
          <p className="text-sm text-text-secondary">{t.admin.smtmPredictionControlHint}</p>
          <div className="admin-inline-actions">
            <span className="admin-status-pill is-neutral">{`${pendingSmtmPredictionChanges} ${t.admin.smtmPredictionPendingChanges}`}</span>
            <button type="button" className="homepage-button-primary" disabled={!pendingSmtmPredictionChanges || smtmPredictionSaving} onClick={saveSmtmPredictionSettings}>{smtmPredictionSaving ? t.admin.smtmPredictionSaving : t.admin.smtmPredictionSaveChanges}</button>
            <button type="button" className="homepage-button-secondary" disabled={!pendingSmtmPredictionChanges || smtmPredictionSaving} onClick={resetSmtmPredictionDrafts}>{t.admin.smtmPredictionResetChanges}</button>
          </div>
          <div className="admin-table-shell admin-smtm-status-table-shell">
            <div className="admin-table-scroll admin-smtm-status-table-scroll">
              <table className="admin-table admin-smtm-table">
                <thead>
                  <tr>
                    <th>{t.admin.smtmInstrument}</th>
                    <th>{t.admin.smtmPredictionMode}</th>
                    <th>{t.admin.smtmPredictionToggle}</th>
                  </tr>
                </thead>
                <tbody>
                  {(smtmPredictionSettings.instruments || []).map((entry) => {
                    const enabled = smtmPredictionDrafts[entry.base_name] ?? Boolean(entry.prediction_enabled);
                    const dirty = smtmPredictionDrafts[entry.base_name] !== undefined && Boolean(entry.prediction_enabled) !== enabled;
                    return (
                      <tr key={entry.base_name}>
                        <td>
                          <div className="font-semibold text-text-primary">{entry.display_name || entry.base_name}</div>
                          <div className="text-xs text-text-muted break-all">{entry.base_name}</div>
                          <span className="admin-status-pill is-neutral">{entry.category}</span>
                        </td>
                        <td>
                          <span className={`admin-status-pill ${enabled ? 'is-ok' : 'is-warning'}`}>{enabled ? t.admin.smtmPredictionEnabled : t.admin.smtmPredictionDisabled}</span>
                          {dirty ? <span className="admin-status-pill is-warning ml-2">{t.admin.smtmPredictionPendingBadge}</span> : null}
                        </td>
                        <td>
                          <label className="admin-check">
                            <input
                              type="checkbox"
                              checked={enabled}
                              disabled={smtmPredictionSaving}
                              onChange={(event) => stageSmtmPredictionSetting(entry.base_name, event.target.checked)}
                            />
                            <span>{t.admin.smtmPredictionToggleLabel}</span>
                          </label>
                        </td>
                      </tr>
                    );
                  })}
                  {!(smtmPredictionSettings.instruments || []).length ? (
                    <tr><td colSpan="3">{t.admin.smtmNoFrames}</td></tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>
        </AdminSection>
      ) : null}
      {activeSection === 'status' ? (
        <AdminSection id="smtm-status" title={t.admin.smtmStatusTitle} subtitle={t.admin.smtmStatusSubtitle}
          count={(smtmStatus.frames || []).length}
          state={(smtmStatus.runtime?.hints || []).length ? (smtmStatus.runtime.hints.map(smtmHintLabel).join(' · ')) : t.admin.smtmNoHints}
          tone={smtmStatus.error ? 'error' : (smtmStatus.runtime?.hints || []).length ? 'warning' : 'ok'}
          actions={<a className="homepage-button-secondary" href={localizePath('/admin/azurite', currentLang)}>{t.admin.smtmOpenAzuriteUi}</a>}>
          {sectionLoading.smtmStatus ? <div className="homepage-muted-panel p-4 text-sm text-text-secondary">{t.auth.validating}</div> : null}
          {smtmStatus.error ? <div className="homepage-muted-panel p-4 text-sm text-error">{t.admin.smtmStatusUnavailable}: {smtmStatus.error}</div> : null}
          <FactGrid items={[
            [t.admin.smtmGeneratedAt, formatTimestamp(smtmStatus.generated_at)],
            [t.admin.smtmMinimumRows, formatInteger(smtmStatus.thresholds?.minimum_training_rows || 100)],
            [t.admin.smtmLatestModelUpload, formatTimestamp(smtmStatus.runtime?.latest_model_upload_at)],
            [t.admin.smtmLatestPredictionWrite, formatTimestamp(smtmStatus.runtime?.latest_prediction_at)],
            [t.admin.smtmBaseFrames, formatInteger(smtmStatus.summary?.base_frames || 0)],
            [t.admin.smtmTrainableFrames, formatInteger(smtmStatus.summary?.trainable_frames || 0)],
            [t.admin.smtmWaitingHistory, formatInteger(smtmStatus.summary?.waiting_for_history || 0)],
            [t.admin.smtmOnlineModels, formatInteger(smtmStatus.summary?.online_models || 0)],
            [t.admin.smtmModelsWithArtifacts, formatInteger(smtmStatus.summary?.models_with_artifacts || 0)],
            [t.admin.smtmPredictionFrames, formatInteger(smtmStatus.summary?.prediction_frames || 0)],
            [t.admin.smtmMissingPredictions, formatInteger(smtmStatus.summary?.prediction_gap_frames || 0)],
            [t.admin.smtmBlobArtifacts, formatInteger(smtmStatus.summary?.blob_total || 0)],
          ]} />
          <div className="admin-table-shell admin-smtm-status-table-shell">
            <div className="admin-table-scroll admin-smtm-status-table-scroll">
              <table className="admin-table admin-smtm-table">
                <thead>
                  <tr>
                    <th>{t.admin.smtmInstrument}</th>
                    <th>{t.admin.smtmPredictionMode}</th>
                    <th>{t.admin.smtmBaseData}</th>
                    <th>{t.admin.smtmMlFrame}</th>
                    <th>{t.admin.smtmModelPublish}</th>
                    <th>{t.admin.smtmPredictionFrame}</th>
                    <th>{t.admin.smtmIssues}</th>
                  </tr>
                </thead>
                <tbody>
                  {(smtmStatus.frames || []).map((frame) => (
                    <tr key={frame.name}>
                      <td>
                        <div className="font-semibold text-text-primary">{frame.display_name || frame.name}</div>
                        <div className="text-xs text-text-muted break-all">{frame.name}</div>
                        <span className="admin-status-pill is-neutral">{frame.category}</span>
                      </td>
                      <td><span className={`admin-status-pill ${frame.prediction_enabled ? 'is-ok' : 'is-warning'}`}>{frame.prediction_enabled ? t.admin.smtmPredictionEnabled : t.admin.smtmPredictionDisabled}</span></td>
                      <td>{renderSmtmFrameMetric(frame.base, frame.training_state)}</td>
                      <td>{renderSmtmFrameMetric(frame.machine_learning, frame.analysis_state)}</td>
                      <td>{renderSmtmModelMetric(frame.model)}</td>
                      <td>{renderSmtmFrameMetric(frame.prediction, frame.prediction_state)}</td>
                      <td>
                        {(frame.issues || []).length ? (
                          <div className="admin-smtm-issue-list">
                            {frame.issues.map((issue) => <span key={`${frame.name}-${issue}`} className="admin-status-pill is-warning">{smtmIssueLabel(issue)}</span>)}
                          </div>
                        ) : <span className="admin-status-pill is-ok">{t.admin.smtmNoIssues}</span>}
                      </td>
                    </tr>
                  ))}
                  {!(smtmStatus.frames || []).length ? (
                    <tr><td colSpan="7">{t.admin.smtmNoFrames}</td></tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>
          {(smtmStatus.rss_feeds || []).length ? (
            <div className="admin-table-shell"><div className="admin-table-scroll">
              <table className="admin-table">
                <thead><tr><th>{t.admin.smtmRssFrames}</th><th>{t.admin.smtmRows}</th><th>{t.admin.smtmLatest}</th><th>{t.admin.smtmAge}</th></tr></thead>
                <tbody>
                  {(smtmStatus.rss_feeds || []).map((feed) => (
                    <tr key={feed.name}>
                      <td className="font-semibold text-text-primary">{feed.name}</td>
                      <td>{formatInteger(feed.row_count || 0)}</td>
                      <td>{formatTimestamp(feed.last_timestamp)}</td>
                      <td>{formatMinutes(feed.minutes_since_latest)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div></div>
          ) : null}
        </AdminSection>
      ) : null}
      {activeSection === 'reporting' ? (
        <AdminSection id="smtm-reporting" title={t.admin.smtmReportingTitle} subtitle={t.admin.smtmReportingSubtitle}
          state={`${t.admin.smtmReportingOpenSignals}: ${formatInteger(smtmReporting.summary?.open_signals || 0)} · ${t.admin.smtmReportingDeliveryFailed}: ${formatInteger(smtmReporting.summary?.delivery_failed || 0)}`}
          tone={smtmReporting.error ? 'error' : Number(smtmReporting.summary?.delivery_failed) > 0 ? 'warning' : 'ok'}
          actions={<span className="text-sm text-text-secondary">{t.admin.smtmGeneratedAt}: {sectionLoading.smtmReporting ? '...' : formatTimestamp(smtmReporting.generated_at)}</span>}>
          {sectionLoading.smtmReporting ? <div className="homepage-muted-panel p-4 text-sm text-text-secondary">{t.auth.validating}</div> : null}
          {smtmReporting.error ? <div className="homepage-muted-panel p-4 text-sm text-error">{t.admin.smtmReportingUnavailable}: {smtmReporting.error}</div> : null}
          <FactGrid items={[
            [t.admin.smtmReportingQualifiedSignals, formatInteger(smtmReporting.summary?.qualified_signals || 0)],
            [t.admin.smtmReportingOpenSignals, formatInteger(smtmReporting.summary?.open_signals || 0)],
            [t.admin.smtmReportingResolvedSignals, formatInteger(smtmReporting.summary?.resolved_signals || 0)],
            [t.admin.smtmReportingWins, formatInteger(smtmReporting.summary?.wins || 0)],
            [t.admin.smtmReportingLosses, formatInteger(smtmReporting.summary?.losses || 0)],
            [t.admin.smtmReportingDeliveryFailed, formatInteger(smtmReporting.summary?.delivery_failed || 0)],
            [t.admin.smtmReportingWinRate, smtmReporting.summary?.win_rate === null || smtmReporting.summary?.win_rate === undefined ? '-' : formatPercent(smtmReporting.summary.win_rate * 100)],
            [t.admin.smtmReportingAverageActualReturn, formatPercent(smtmReporting.summary?.average_actual_return_pct)],
            [t.admin.smtmReportingNetActualReturn, formatPercent(smtmReporting.summary?.net_actual_return_pct)],
            ...Object.entries(smtmReporting.summary?.horizon_summary || {}).map(([key, horizon]) => [
              horizon.label || key,
              `${formatPercent(horizon.average_return_pct)} · ${t.admin.smtmReportingResolvedSignals}: ${formatInteger(horizon.resolved || 0)} · ${t.admin.smtmReportingWinRate}: ${horizon.win_rate === null || horizon.win_rate === undefined ? '-' : formatPercent(horizon.win_rate * 100)}`,
            ]),
          ]} />
          <div className="admin-table-shell">
            <div className="admin-table-scroll admin-smtm-status-table-scroll">
              <table className="admin-table admin-smtm-table">
                <thead>
                  <tr>
                    <th>{t.admin.smtmInstrument}</th>
                    <th>{t.admin.smtmDirection}</th>
                    <th>{t.admin.smtmReportingEntryTime}</th>
                    <th>{t.admin.smtmReportingExitTime}</th>
                    <th>{t.admin.smtmReportingExpectedReturn}</th>
                    <th>{t.admin.smtmReportingActualReturn}</th>
                    <th>{t.admin.smtmReportingSignalStatus}</th>
                    <th>{t.admin.smtmSentiment}</th>
                  </tr>
                </thead>
                <tbody>
                  {(smtmReporting.recent_signals || []).map((signal) => (
                    <tr key={signal.signal_id}>
                      <td>
                        <div className="font-semibold text-text-primary">{signal.display_label || signal.instrument_name}</div>
                        <div className="text-xs text-text-muted break-all">{signal.signal_id}</div>
                      </td>
                      <td>{signal.direction || '-'}</td>
                      <td>{formatTimestamp(signal.source_time)}</td>
                      <td>{formatTimestamp(signal.target_time)}</td>
                      <td>{formatPercent(signal.expected_return_pct)}</td>
                      <td>{formatPercent(signal.actual_return_pct)}</td>
                      <td><span className={`admin-status-pill ${smtmStateClass(signal.status)}`}>{smtmStateLabel(signal.status)}</span></td>
                      <td>{signal.sentiment_emotion || '-'}</td>
                    </tr>
                  ))}
                  {!(smtmReporting.recent_signals || []).length ? (
                    <tr><td colSpan="8">{t.admin.smtmReportingNoSignals}</td></tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>
          {(smtmReporting.recent_delivery_failures || []).length || (smtmReporting.recent_pending_deliveries || []).length ? (
            <div className="admin-pair">
              <div className="space-y-2">
                <h3>{t.admin.smtmReportingPendingDeliveries}</h3>
                <div className="admin-table-shell">
                  <div className="admin-table-scroll">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>{t.admin.smtmDeliveryStage}</th>
                          <th>{t.admin.smtmDeliveryRecipient}</th>
                          <th>{t.admin.smtmReportingSignalStatus}</th>
                          <th>{t.admin.smtmLatest}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(smtmReporting.recent_pending_deliveries || []).map((delivery) => (
                          <tr key={`pending-${delivery.id}`}>
                            <td>{delivery.delivery_stage}</td>
                            <td>{delivery.notification_email}</td>
                            <td><span className={`admin-status-pill ${smtmDeliveryClass(delivery.status)}`}>{delivery.status}</span></td>
                            <td>{formatTimestamp(delivery.updated_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <h3>{t.admin.smtmReportingFailedDeliveries}</h3>
                <div className="admin-table-shell">
                  <div className="admin-table-scroll">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>{t.admin.smtmDeliveryStage}</th>
                          <th>{t.admin.smtmDeliveryRecipient}</th>
                          <th>{t.admin.smtmReportingDeliveryError}</th>
                          <th>{t.admin.smtmLatest}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(smtmReporting.recent_delivery_failures || []).map((delivery) => (
                          <tr key={`failed-${delivery.id}`}>
                            <td>{delivery.delivery_stage}</td>
                            <td>{delivery.notification_email}</td>
                            <td>{delivery.error || '-'}</td>
                            <td>{formatTimestamp(delivery.updated_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </AdminSection>
      ) : null}
      {activeSection === 'training' ? (
        <AdminSection id="smtm-training" title={t.admin.smtmTrainingTitle} subtitle={t.admin.smtmTrainingSubtitle}
          count={smtmTraining.summary?.model_count || 0}
          state={`${t.admin.smtmTrainingOnlineModels}: ${formatInteger(smtmTraining.summary?.online_models || 0)} · ${t.admin.smtmTrainingFailedModels}: ${formatInteger(smtmTraining.summary?.failed_models || 0)}`}
          tone={smtmTraining.error ? 'error' : Number(smtmTraining.summary?.failed_models) > 0 ? 'warning' : 'ok'}
          actions={<span className="text-sm text-text-secondary">{t.admin.smtmGeneratedAt}: {sectionLoading.smtmTraining ? '...' : formatTimestamp(smtmTraining.generated_at)}</span>}>
          {sectionLoading.smtmTraining ? <div className="homepage-muted-panel p-4 text-sm text-text-secondary">{t.auth.validating}</div> : null}
          {smtmTraining.error ? <div className="homepage-muted-panel p-4 text-sm text-error">{t.admin.smtmTrainingUnavailable}: {smtmTraining.error}</div> : null}
          <FactGrid items={[
            [t.admin.smtmTrainingModels, formatInteger(smtmTraining.summary?.model_count || 0)],
            [t.admin.smtmTrainingOnlineModels, formatInteger(smtmTraining.summary?.online_models || 0)],
            [t.admin.smtmTrainingFailedModels, formatInteger(smtmTraining.summary?.failed_models || 0)],
            [t.admin.smtmTrainingStaleModels, formatInteger(smtmTraining.summary?.stale_models || 0)],
            [t.admin.smtmTrainingAverageMae, formatMetric(smtmTraining.summary?.average_mae)],
            [t.admin.smtmTrainingAverageRmse, formatMetric(smtmTraining.summary?.average_rmse)],
            [t.admin.smtmTrainingAverageR2, formatMetric(smtmTraining.summary?.average_r2)],
            [t.admin.smtmTrainingAverageMase, formatMetric(smtmTraining.summary?.average_mase, 3)],
            [t.admin.smtmTrainingAverageHitRate, formatPercent(smtmTraining.summary?.average_directional_accuracy === null || smtmTraining.summary?.average_directional_accuracy === undefined ? null : smtmTraining.summary.average_directional_accuracy * 100)],
          ]} />
          {(smtmTraining.models || []).length ? (
            <div className="admin-smtm-training-workbench">
              <section className="homepage-muted-panel p-4 admin-smtm-training-list-panel">
                <h3>{t.admin.smtmTrainingModels}</h3>
                <div className="admin-smtm-training-list-scroll">
                  {(smtmTraining.models || []).map((model) => (
                    <button
                      key={model.model_name}
                      type="button"
                      className={`admin-smtm-training-list-item ${selectedSmtmTrainingModel?.model_name === model.model_name ? 'active' : ''}`}
                      onClick={() => setSelectedSmtmTrainingModelName(model.model_name)}
                    >
                      <div className="admin-smtm-training-list-head">
                        <strong>{model.display_label || model.instrument_name}</strong>
                        <span className={`admin-status-pill ${smtmStateClass(model.status)}`}>{smtmStateLabel(model.status)}</span>
                      </div>
                      <div className="text-xs text-text-muted break-all">{model.model_name}</div>
                      <div className="text-sm text-text-secondary">{`${t.admin.smtmTrainingRows}: ${formatInteger(model.training_rows || 0)} / ${formatInteger(model.test_rows || 0)}`}</div>
                      <div className="text-sm text-text-secondary">{`MAE ${formatMetric(model.mae)} | RMSE ${formatMetric(model.rmse)} | R² ${formatMetric(model.r2_score)}`}</div>
                      <div className="text-sm text-text-secondary">{`MASE ${formatMetric(model.mase, 3)} | ${t.admin.smtmTrainingHitRate} ${formatPercent(model.directional_accuracy === null || model.directional_accuracy === undefined ? null : model.directional_accuracy * 100)}`}</div>
                    </button>
                  ))}
                </div>
              </section>

              <section className="homepage-muted-panel p-4 admin-smtm-training-detail-panel">
                {selectedSmtmTrainingModel ? (
                  <div className="admin-smtm-training-detail-scroll">
                    <div className="admin-smtm-training-detail-header">
                      <div>
                        <h3>{selectedSmtmTrainingModel.display_label || selectedSmtmTrainingModel.instrument_name}</h3>
                        <div className="text-xs text-text-muted break-all">{selectedSmtmTrainingModel.model_name}</div>
                      </div>
                      <span className={`admin-status-pill ${smtmStateClass(selectedSmtmTrainingModel.status)}`}>{smtmStateLabel(selectedSmtmTrainingModel.status)}</span>
                    </div>

                    <FactGrid items={[
                      [t.admin.smtmTrainingLastSuccess, formatTimestamp(selectedSmtmTrainingModel.last_success_at)],
                      [t.admin.smtmLatestModelUpload, formatTimestamp(selectedSmtmTrainingModel.latest_upload_at)],
                      [t.admin.smtmTrainingResolution, `${selectedSmtmTrainingModel.resolution || '-'} | ${formatInteger(selectedSmtmTrainingModel.horizon_minutes || 0)}m`],
                      [t.admin.smtmTrainingRows, `${formatInteger(selectedSmtmTrainingModel.training_rows || 0)} / ${formatInteger(selectedSmtmTrainingModel.test_rows || 0)}`],
                      ['MAE', formatMetric(selectedSmtmTrainingModel.mae)],
                      ['RMSE', formatMetric(selectedSmtmTrainingModel.rmse)],
                      ['R²', formatMetric(selectedSmtmTrainingModel.r2_score)],
                      ['MSE', formatMetric(selectedSmtmTrainingModel.mse)],
                      ['MASE', formatMetric(selectedSmtmTrainingModel.mase, 3)],
                      [t.admin.smtmTrainingHitRate, formatPercent(selectedSmtmTrainingModel.directional_accuracy === null || selectedSmtmTrainingModel.directional_accuracy === undefined ? null : selectedSmtmTrainingModel.directional_accuracy * 100)],
                    ]} />

                    <div className="admin-smtm-training-detail-grid">
                      <article className="admin-smtm-training-card admin-smtm-training-card-tall">
                        <div className="admin-kpi-label">{t.admin.smtmTrainingLlmFeatures}</div>
                        {(selectedSmtmTrainingModel.llm_features || []).length ? (
                          <div className="admin-smtm-issue-list">
                            {(selectedSmtmTrainingModel.llm_features || []).map((feature) => (
                              <span key={`${selectedSmtmTrainingModel.model_name}-${feature}`} className="admin-status-pill is-neutral">{feature}</span>
                            ))}
                          </div>
                        ) : <div className="text-sm text-text-secondary">-</div>}
                        {Object.keys(selectedSmtmTrainingModel.llm_feature_importance || {}).length ? (
                          <div className="admin-smtm-training-detail-list">
                            {Object.entries(selectedSmtmTrainingModel.llm_feature_importance || {}).slice(0, 12).map(([feature, value]) => (
                              <div key={`${selectedSmtmTrainingModel.model_name}-${feature}`} className="admin-smtm-training-detail-row">
                                <span>{feature}</span>
                                <span>{formatMetric(value)}</span>
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </article>

                      <article className="admin-smtm-training-card admin-smtm-training-card-tall">
                        <div className="admin-kpi-label">{t.admin.smtmArtifacts}</div>
                        {(selectedSmtmTrainingModel.artifact_files || []).length ? (
                          <div className="admin-smtm-training-detail-list">
                            {(selectedSmtmTrainingModel.artifact_files || []).map((artifact, index) => (
                              <div key={`${selectedSmtmTrainingModel.model_name}-artifact-${index}`} className="admin-smtm-training-detail-row">
                                <span>{artifactLabel(artifact)}</span>
                              </div>
                            ))}
                          </div>
                        ) : <div className="text-sm text-text-secondary">-</div>}
                      </article>

                      <article className="admin-smtm-training-card admin-smtm-training-card-wide">
                        <div className="admin-kpi-label">{t.admin.smtmTrainingIssues}</div>
                        {(selectedSmtmTrainingModel.issues || []).length || selectedSmtmTrainingModel.error ? (
                          <div className="admin-smtm-issue-list">
                            {(selectedSmtmTrainingModel.issues || []).map((issue) => <span key={`${selectedSmtmTrainingModel.model_name}-${issue}`} className="admin-status-pill is-warning">{smtmIssueLabel(issue)}</span>)}
                            {selectedSmtmTrainingModel.error ? <span className="admin-status-pill is-missing">{selectedSmtmTrainingModel.error}</span> : null}
                          </div>
                        ) : <span className="admin-status-pill is-ok">{t.admin.smtmNoIssues}</span>}
                      </article>
                    </div>
                  </div>
                ) : <div className="text-sm text-text-secondary">{t.admin.smtmTrainingNoModels}</div>}
              </section>
            </div>
          ) : (
            <div className="homepage-muted-panel p-4 text-sm text-text-secondary">{t.admin.smtmTrainingNoModels}</div>
          )}
        </AdminSection>
      ) : null}
      {activeSection === 'console' ? (
        <AdminSection id="smtm-console" title={t.admin.smtmTrainingConsoleTitle} subtitle={t.admin.smtmTrainingConsoleSubtitle} count={(smtmTrainingConsole.lines || []).length}
          actions={<span className="text-sm text-text-secondary">{t.admin.smtmGeneratedAt}: {sectionLoading.smtmTrainingConsole ? '...' : formatTimestamp(smtmTrainingConsole.generated_at)}</span>}>
          {sectionLoading.smtmTrainingConsole ? <div className="homepage-muted-panel p-4 text-sm text-text-secondary">{t.auth.validating}</div> : null}
          {smtmTrainingConsole.error ? <div className="homepage-muted-panel p-4 text-sm text-error">{t.admin.smtmTrainingConsoleUnavailable}: {smtmTrainingConsole.error}</div> : null}
          <div className="homepage-muted-panel p-4 admin-log">
            {(smtmTrainingConsole.lines || []).length ? (
              (smtmTrainingConsole.lines || []).map((line, index) => (
                <div key={`${line.time || 'time'}-${index}`}>
                  {`${line.time || '-'} | ${line.level || 'INFO'} | ${line.message || ''}`}
                </div>
              ))
            ) : (
              <div className="text-text-secondary">{t.admin.smtmTrainingConsoleEmpty}</div>
            )}
          </div>
        </AdminSection>
      ) : null}
    </>
  );

  const usersPage = (
    <>
      <AdminSection id="users-create" title={t.admin.createUser} subtitle={t.admin.usersCreateHint} collapsed>
        <form className="admin-form-grid" onSubmit={handleCreateUser}>
          <input className="form-input" placeholder={t.admin.userEmail} value={createUser.email} onChange={(event) => setCreateUser((current) => ({ ...current, email: event.target.value }))} required />
          <input className="form-input" placeholder={t.admin.userName} value={createUser.username} onChange={(event) => setCreateUser((current) => ({ ...current, username: event.target.value }))} required />
          <input className="form-input" type="password" minLength={12} title={t.auth.errors.passwordTooWeak} placeholder={t.admin.setPassword} value={createUser.password} onChange={(event) => setCreateUser((current) => ({ ...current, password: event.target.value }))} required />
          <div className="admin-form-grid-wide space-y-2">
            <div className="text-sm text-text-muted">{t.admin.userRoles}</div>
            {renderRoleCheckboxes(createUser.roles, (roles) => setCreateUser((current) => ({ ...current, roles })))}
          </div>
          <div><button className="homepage-button-primary" type="submit">{t.admin.createUser}</button></div>
        </form>
      </AdminSection>

      <AdminSection id="users-list" title={t.admin.usersTitle} subtitle={t.admin.usersSubtitle} count={users.length}
        state={`${t.admin.sessionActiveUsers}: ${formatInteger(sessionSummary.summary?.active_session_users ?? 0)} · ${t.admin.sessionActiveSessions}: ${formatInteger(sessionSummary.summary?.active_sessions ?? 0)} · ${t.admin.sessionMfaSessions}: ${formatInteger(sessionSummary.summary?.active_mfa_sessions ?? 0)}`}>
        <div className="admin-user-toolbar">
          <input
            className="form-input admin-user-search"
            placeholder={t.admin.filterUsers}
            value={filter}
            onChange={(event) => { setFilter(event.target.value); setUserPage(0); }}
          />
          <select
            className="form-input admin-user-select"
            value={userStatusFilter}
            onChange={(event) => { setUserStatusFilter(event.target.value); setUserPage(0); }}
          >
            <option value="">{t.admin.usersFilterAllStatus}</option>
            {availableStatuses.map((status) => <option key={status} value={status}>{formatStatus(status)}</option>)}
          </select>
          <select
            className="form-input admin-user-select"
            value={userRoleFilter}
            onChange={(event) => { setUserRoleFilter(event.target.value); setUserPage(0); }}
          >
            <option value="">{t.admin.usersFilterAllRoles}</option>
            {availableRoles.map((role) => <option key={role} value={role}>{t.admin.roleLabels?.[role] || role}</option>)}
          </select>
          <span className="admin-user-count">{filteredUsers.length} / {users.length}</span>
        </div>

        {sectionLoading.users ? <div className="homepage-muted-panel p-4 text-sm text-text-secondary">{t.auth.validating}</div> : null}

        <div className="admin-table-shell">
          <div className="admin-table-scroll">
            <table className="admin-table admin-user-table">
              <thead>
                <tr>
                  <th>{t.admin.userName}</th>
                  <th>{t.admin.userRoles}</th>
                  <th>{t.admin.userStatus}</th>
                  <th>{t.admin.userMfa}</th>
                  <th>{t.admin.userCreatedAt}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {pagedUsers.map((user) => (
                  <tr key={user.id} className={user.id === selectedUserId ? 'is-selected' : ''}>
                    <td>
                      <div className="admin-user-name">{user.username}</div>
                      <div className="admin-user-mail" title={user.email}>{user.email}</div>
                    </td>
                    <td>
                      <div className="admin-user-roles">
                        {normaliseRoleList(user.roles, availableRoles).map((role) => (
                          <span key={role} className="admin-status-pill">{t.admin.roleLabels?.[role] || role}</span>
                        ))}
                      </div>
                    </td>
                    <td><span className={`admin-status-pill ${userStatusClass(user.status)}`}>{formatStatus(user.status)}</span></td>
                    <td><span className={`admin-status-pill ${user.mfa_configured ? 'is-ok' : 'is-warning'}`}>{user.mfa_configured ? t.admin.userMfaOn : t.admin.userMfaOff}</span></td>
                    <td className="admin-user-created">{formatDay(user.created_at)}</td>
                    <td>
                      <button
                        className="homepage-button-secondary"
                        type="button"
                        onClick={() => setSelectedUserId(user.id === selectedUserId ? '' : user.id)}
                      >
                        {user.id === selectedUserId ? t.admin.usersClose : t.admin.usersManage}
                      </button>
                    </td>
                  </tr>
                ))}
                {!pagedUsers.length ? <tr><td colSpan="6">{t.admin.usersNoneFound}</td></tr> : null}
              </tbody>
            </table>
          </div>
        </div>

        {userPageCount > 1 ? (
          <div className="admin-user-pager">
            <button className="homepage-button-secondary" type="button" disabled={currentUserPage === 0} onClick={() => setUserPage(currentUserPage - 1)}>{t.admin.usersPagePrev}</button>
            <span>{currentUserPage * USERS_PER_PAGE + 1}–{Math.min((currentUserPage + 1) * USERS_PER_PAGE, filteredUsers.length)} / {filteredUsers.length}</span>
            <button className="homepage-button-secondary" type="button" disabled={currentUserPage >= userPageCount - 1} onClick={() => setUserPage(currentUserPage + 1)}>{t.admin.usersPageNext}</button>
          </div>
        ) : null}

        {selectedUser ? (() => {
          const actionState = userActions[selectedUser.id] || getUserActionDefaults(selectedUser.id);
          return (
            <div className="homepage-muted-panel p-5 space-y-4 admin-user-detail">
              <div className="admin-section-head">
                <div>
                  <h3>{selectedUser.username}</h3>
                  <p className="site-copy">{selectedUser.email}</p>
                </div>
                <button className="homepage-button-secondary" type="button" onClick={() => setSelectedUserId('')}>{t.admin.usersClose}</button>
              </div>

              <div className="admin-form-row">
                <input className="form-input" placeholder={t.admin.setPassword} type="password" minLength={12} title={t.auth.errors.passwordTooWeak} value={actionState.password} onChange={(event) => updateUserAction(selectedUser.id, 'password', event.target.value)} />
                <select className="form-input" value={actionState.status} onChange={(event) => updateUserAction(selectedUser.id, 'status', event.target.value)}>
                  {availableStatuses.map((status) => <option key={status} value={status}>{formatStatus(status)}</option>)}
                </select>
              </div>

              <div className="space-y-2">
                <div className="text-sm text-text-muted">{t.admin.userRoles}</div>
                {renderRoleCheckboxes(actionState.roles, (roles) => updateUserAction(selectedUser.id, 'roles', roles))}
              </div>

              <div className="admin-inline-actions">
                <button className="homepage-button-primary" type="button" onClick={() => runUserAction(selectedUser.id, 'password')}>{t.admin.setPassword}</button>
                <button className="homepage-button-secondary" type="button" onClick={() => runUserAction(selectedUser.id, 'roles')}>{t.admin.setRoles}</button>
                <button className="homepage-button-secondary" type="button" onClick={() => runUserAction(selectedUser.id, 'status')}>{t.admin.setStatus}</button>
                <button className="homepage-button-secondary" type="button" onClick={() => runUserAction(selectedUser.id, 'mfa')}>{t.admin.resetMfa}</button>
                <button className="homepage-button-secondary is-destructive" type="button" onClick={() => runUserAction(selectedUser.id, 'sessions')}>{t.admin.revokeSessions}</button>
                <button className="homepage-button-secondary is-destructive" type="button" onClick={() => runUserAction(selectedUser.id, 'delete')}>{t.admin.deleteUser}</button>
              </div>
            </div>
          );
        })() : null}
      </AdminSection>
    </>
  );

  const mailPage = (
    <AdminSection id="mail" title={t.admin.mailTitle} subtitle={t.admin.aiMailSubtitle}
      state={`${sectionLoading.mailSettings ? '...' : mailSettings.configured ? t.admin.statusConfigured : t.admin.statusMissing}${mailSettings.source ? ` · ${t.admin.mailSource}: ${mailSettings.source}` : ''}`}
      tone={sectionLoading.mailSettings ? '' : mailSettings.configured ? 'ok' : 'warning'}>
      {sectionLoading.mailSettings ? <div className="homepage-muted-panel p-4 text-sm text-text-secondary">{t.auth.validating}</div> : null}
      <form className="admin-form-grid" onSubmit={saveMailSettings}>
        <label className="admin-field">{t.admin.smtpHost}<input className="form-input" value={mailSettings.smtp_host} onChange={(event) => setMailSettings((current) => ({ ...current, smtp_host: event.target.value }))} /></label>
        <label className="admin-field">{t.admin.smtpPort}<input className="form-input" value={mailSettings.smtp_port} onChange={(event) => setMailSettings((current) => ({ ...current, smtp_port: event.target.value }))} /></label>
        <label className="admin-field">{t.admin.smtpSecurity}<input className="form-input" value={mailSettings.smtp_security} onChange={(event) => setMailSettings((current) => ({ ...current, smtp_security: event.target.value }))} /></label>
        <label className="admin-field">{t.admin.smtpUsername}<input className="form-input" value={mailSettings.smtp_username} onChange={(event) => setMailSettings((current) => ({ ...current, smtp_username: event.target.value }))} /></label>
        <label className="admin-field">{t.admin.smtpPassword}<input className="form-input" type="password" value={mailSettings.smtp_password || ''} onChange={(event) => setMailSettings((current) => ({ ...current, smtp_password: event.target.value }))} /></label>
        <label className="admin-field">{t.admin.fromEmail}<input className="form-input" value={mailSettings.from_email} onChange={(event) => setMailSettings((current) => ({ ...current, from_email: event.target.value }))} /></label>
        <label className="admin-field">{t.admin.fromName}<input className="form-input" value={mailSettings.from_name} onChange={(event) => setMailSettings((current) => ({ ...current, from_name: event.target.value }))} /></label>
        <label className="admin-field">{t.admin.replyToEmail}<input className="form-input" value={mailSettings.reply_to_email} onChange={(event) => setMailSettings((current) => ({ ...current, reply_to_email: event.target.value }))} /></label>
        <div className="admin-form-grid-wide admin-form-row">
          <button className="homepage-button-primary" type="submit">{t.admin.saveMailSettings}</button>
          <input className="form-input" placeholder={t.admin.testRecipient} value={testRecipient} onChange={(event) => setTestRecipient(event.target.value)} />
          <button className="homepage-button-secondary" type="button" onClick={sendTestMail}>{t.admin.sendTestMail}</button>
        </div>
      </form>
    </AdminSection>
  );

  const auditPage = (
    <AdminSection id="audit" title={t.admin.auditTitle} count={audit.length}>
      {sectionLoading.audit ? <div className="homepage-muted-panel p-4 text-sm text-text-secondary">{t.auth.validating}</div> : null}
      <div className="admin-table-shell">
        <div className="admin-table-scroll">
          <table className="admin-table admin-fixed-table">
            <colgroup><col style={{ width: '22%' }} /><col style={{ width: '16%' }} /><col style={{ width: '62%' }} /></colgroup>
            <thead>
              <tr>
                <th>{t.admin.auditEvent}</th>
                <th>{t.admin.auditCreatedAt}</th>
                <th>{t.admin.auditDetails}</th>
              </tr>
            </thead>
            <tbody>
              {audit.slice(0, auditShown).map((event) => (
                <tr key={event.id}>
                  <td>{event.event_type}</td>
                  <td className="admin-user-created">{formatTimestamp(event.created_at)}</td>
                  <td className="admin-cell-clip text-xs text-text-secondary" title={JSON.stringify(event.details, null, 2)}>{JSON.stringify(event.details)}</td>
                </tr>
              ))}
              {!audit.length && !sectionLoading.audit ? <tr><td colSpan="3">{n.none}</td></tr> : null}
            </tbody>
          </table>
        </div>
      </div>
      <ShowMore shown={auditShown} total={audit.length} onMore={auditMore} />
    </AdminSection>
  );
  // -- the first page: the company's day (IDD D-HP-ADMIN-040) ------------------------------------------------------

  const org = attention.organisation;
  const botOf = (key) => (org?.positions || []).find((position) => position.key === key)?.bot || key;
  const company = attention.company;
  const coreData = company?.core?.data;
  const word = today.word || (today.timeline || []).find((item) => item.kind === 'word');
  const working = (today.workItems || []).filter((item) => item.status === 'in_progress');
  const lastMeeting = (today.meetings || [])[0] || null;
  const berlinHour = Number(new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Berlin', hour: '2-digit', hourCycle: 'h23' }).format(new Date()));
  const nextMeetingHour = (berlinHour + (berlinHour % 2 === 0 ? 2 : 1)) % 24;
  const stateLines = [];
  const pushState = (key, label, text, tab, section = '', tone = 'ok') => stateLines.push({ key, label, text, tab, section, tone });
  if (!sectionLoading.platform && !platform.error) {
    pushState('platform', t.admin.tabPlatform, fill(a.state?.platform, {
      ready: formatInteger(platform.summary?.ready ?? 0), workloads: formatInteger(platform.summary?.workloads ?? 0),
      nodes: formatInteger((platform.nodes || []).filter((node) => node.ready).length), total: formatInteger((platform.nodes || []).length),
      restarts: formatInteger(platform.summary?.restarts ?? 0),
    }), 'workloads', '', (platform.summary?.degraded ?? 0) > 0 ? 'warning' : 'ok');
  }
  if (!sectionLoading.aiGateway) {
    pushState('gateway', t.admin.tabEnvoy, fill(a.state?.gateway, {
      state: aiGateway.gateway?.programmed ? t.admin.statusConfigured : t.admin.statusMissing,
      models: formatInteger((aiGateway.models || []).length),
      backends: formatInteger((aiGateway.backends || []).filter((entry) => entry.ready >= entry.desired && entry.desired > 0).length),
      total: formatInteger((aiGateway.backends || []).length),
    }), 'gateway', '', aiGateway.gateway?.programmed ? 'ok' : 'error');
    const engineRows = (aiGateway.engines || []).flatMap((engine) => (engine.available && engine.models?.length ? engine.models.map((row) => ({ ...row, backend: engine.name })) : []));
    pushState('engines', n.pages?.engines, engineRows.length
      ? engineRows.map((row) => fill(a.state?.engine, { engine: displayModelName(row.model) || row.backend, running: formatInteger(row.running), waiting: formatInteger(row.waiting) })).join('; ')
      : t.admin.aiEngineUnavailable, 'engines', '', engineRows.length ? 'ok' : 'warning');
  }
  if (!sectionLoading.aiUsage && !aiUsage.error) {
    pushState('usage', n.pages?.usage, fill(a.state?.usage, { tokens: formatInteger(aiUsage.summary?.total_tokens ?? 0), identities: formatInteger(aiUsage.summary?.identities ?? 0), window: aiUsage.window || '-' }), 'usage', 'identities');
  }
  if (smtmPredictionRuntime.generated_at) {
    pushState('smtm', t.admin.tabSmtm, fill(a.state?.smtm, {
      gate: smtmPredictionRuntime.runtime?.prediction_gate_open ? t.admin.smtmRuntimeStateOpen : t.admin.smtmRuntimeStateClosed,
      rebuild: smtmPredictionRuntime.runtime?.rebuild_active ? t.admin.smtmRuntimeStateActive : t.admin.smtmRuntimeStateIdle,
      online: formatInteger(smtmTraining.summary?.online_models ?? 0), models: formatInteger(smtmTraining.summary?.model_count ?? 0),
      failed: formatInteger(smtmTraining.summary?.failed_models ?? 0),
    }), 'smtm', 'runtime', smtmPredictionRuntime.runtime?.prediction_gate_open && !smtmPredictionRuntime.runtime?.rebuild_active ? 'ok' : 'warning');
  }
  if (smtmReporting.generated_at) {
    pushState('smtm-signals', t.admin.smtmReportingTitle, fill(a.state?.smtmSignals, {
      open: formatInteger(smtmReporting.summary?.open_signals ?? 0), resolved: formatInteger(smtmReporting.summary?.resolved_signals ?? 0),
      failed: formatInteger(smtmReporting.summary?.delivery_failed ?? 0),
    }), 'smtm', 'reporting', Number(smtmReporting.summary?.delivery_failed) > 0 ? 'warning' : 'ok');
  }
  if (attention.smtmMcp) {
    const smtmServers = attention.smtmMcp.items || [];
    pushState('veridion-smtm', t.admin.tabVeridion, fill(a.state?.smtmVeridion, {
      connected: formatInteger(smtmServers.filter((server) => server.status === 'connected').length), servers: formatInteger(smtmServers.length),
    }), 'agentic', 'mcp', smtmServers.some((server) => server.status === 'unreachable') ? 'error' : 'ok');
  }
  if (company) {
    const companyWorkloads = company.workloads?.data || [];
    const counts = coreData?.agents || {};
    pushState('company', t.admin.companyTitle, fill(a.state?.company, {
      status: company.health?.data?.status || '-',
      ready: formatInteger(companyWorkloads.filter((workload) => workload.state === 'ready').length), workloads: formatInteger(companyWorkloads.length),
      core: coreData?.core?.status || '-',
      agents: formatInteger(['building', 'starting', 'running', 'waiting', 'idle', 'paused'].reduce((sum, key) => sum + (counts[key] || 0), 0)),
      waiting: formatInteger(counts.waiting || 0),
    }), 'core', '', company.health?.error || coreData?.core?.emergencyStop ? 'error' : 'ok');
    const bots = company.webex?.data?.bots || [];
    pushState('webex', n.pages?.webex, fill(a.state?.companyWebex, { registered: formatInteger(bots.filter((bot) => bot.webhook?.registered).length), bots: formatInteger(bots.length) }),
      'webex', '', bots.some((bot) => !bot.webhook?.registered) ? 'warning' : 'ok');
  }
  if (org || attention.tasks) {
    const openApprovals = (org?.approvals || []).length;
    const waitingTasks = ((attention.tasks?.items) || []).filter((task) => ['waiting', 'needs_person', 'review'].includes(task.status)).length;
    pushState('approvals', n.sections?.organisation?.approvals, fill(a.state?.approvals, { approvals: formatInteger(openApprovals), tasks: formatInteger(waitingTasks) }),
      'organisation', 'approvals', openApprovals || waitingTasks ? 'warning' : 'ok');
  }
  if (!sectionLoading.sessionSummary) {
    pushState('sessions', t.admin.tabUsers, fill(a.state?.sessions, {
      users: formatInteger(sessionSummary.summary?.active_session_users ?? 0), sessions: formatInteger(sessionSummary.summary?.active_sessions ?? 0),
      mfa: formatInteger(sessionSummary.summary?.active_mfa_sessions ?? 0), total: formatInteger(sessionSummary.summary?.total_users ?? 0),
    }), 'users');
  }
  if (!sectionLoading.mailSettings) {
    pushState('mail', t.admin.tabMail, fill(a.state?.mail, { state: mailSettings.configured ? t.admin.statusConfigured : t.admin.statusMissing }), 'mail', '', mailSettings.configured ? 'ok' : 'warning');
  }

  const todayPage = (
    <>
      {/* The two company switches, visible where the day starts (owner UX): the autopilot (on it there is no human
          decision, IDD D-VCORE-295) and the emergency stop — its own function, nothing to do with the autopilot. */}
      <div className="site-card admin-panel p-5 space-y-2">
        <div className="font-bold text-base">
          {currentLang === 'de'
            ? 'Leitstand & Systemübersicht der pAIoneer Labs GmbH'
            : 'Executive Operations Center & System Overview — pAIoneer Labs GmbH'}
        </div>
        <p className="text-xs text-text-secondary">
          {currentLang === 'de'
            ? 'Dieses Administrations-Dashboard bündelt die Steuerung unserer gesamten KI- und Cloud-Plattform: (1) Unter „Tagesübersicht & Leitstand“ steuern Sie den Autopiloten und sehen offene Systemhinweise sowie aktive Arbeitspakete. (2) Unter „Live-Vorschauen & Modellarchitektur“ sowie „Data Lake & KI-Modelltraining“ verwalten Sie unsere eigene multimodale KI-Modellarchitektur (pAIoneer-Omni-14B-A4B-MPO), den Block-Storage-Data-Lake (< 100 GB lokaler GPU-Cache) und das GPU-Training im 41,3-GB-VRAM-Budget. (3) Unter „Firmengedächtnis“ sehen Sie die fachlich getrennten Abteilungs-Wissensdomänen je Geschäftsbereich und das wissenschaftliche Forschungsarchiv.'
            : 'This administration dashboard centralizes control across our entire AI and cloud platform: (1) Under "Executive Overview & Operations" you manage the autopilot, system alerts, and active work packages. (2) Under "Live Previews & Model Architecture" and "Data Lake & AI Model Training" you operate our sovereign multimodal AI model architecture (pAIoneer-Omni-14B-A4B-MPO), the Block Storage Data Lake (< 100 GB local GPU cache), and GPU training within the freed 41.3 GB VRAM budget. (3) Under "Company Memory" you inspect the isolated Business Unit Knowledge Domains and the Quantum & Frontier AI Research Repository.'}
        </p>
      </div>
      <TodayControls t={t} title={t.admin.nav?.controlsTitle} />
      <SystemLlmAllocationPanel compact />
      <AdminSection id="today-attention" title={n.attentionTitle} subtitle={n.attentionSubtitle} count={attentionItems.length}
        tone={attentionItems.some((item) => item.tone === 'error') ? 'error' : attentionItems.length ? 'warning' : 'ok'}
        state={attentionItems.length ? '' : (attention.loaded ? n.attentionNone : t.auth.validating)}>
        {attentionItems.length ? (
          <div className="admin-attention-list">
            {attentionItems.map((item, index) => (
              <button key={`${item.tab}-${item.section}-${index}`} type="button" className="admin-attention-item" onClick={() => writeAdminRoute(item.tab, item.section)}>
                <span className={`admin-status-pill ${item.tone === 'error' ? 'is-missing' : 'is-warning'}`}>{item.tone === 'error' ? a.toneError : a.toneWarning}</span>
                <span className="admin-attention-text">{item.text}</span>
                <span className="admin-attention-where">{tabLabels[item.tab]}{item.section && n.sections?.[item.tab]?.[item.section] ? ` · ${n.sections[item.tab][item.section]}` : ''}</span>
              </button>
            ))}
          </div>
        ) : null}
      </AdminSection>

      <AdminSection id="today-word" title={n.today?.wordTitle} state={word ? `${word.who || word.actor || '-'} · ${formatTimestamp(word.at)}` : (today.loaded ? n.today?.wordNone : '')}
        actions={<AdminLink tab="company-memory" section="day">{t.admin.memory?.day?.title}</AdminLink>}>
        {word ? <blockquote className="admin-quote">{word.text}</blockquote> : null}
        {word?.title ? <div className="text-sm text-text-secondary">{word.title}</div> : null}
      </AdminSection>

      <AdminSection id="today-since" title={n.today?.sinceTitle} count={(today.timeline || []).length}
        state={fill(n.today?.sinceState, { time: formatTimestamp(today.since, { seconds: false }) })}
        actions={<AdminLink tab="company-memory" section="day">{t.admin.memory?.day?.title}</AdminLink>}>
        {today.error ? <div className="homepage-muted-panel p-4 text-sm text-error">{t.admin.memory?.unavailable} ({today.error})</div> : null}
        <div className="admin-table-shell"><div className="admin-table-scroll">
          <table className="admin-table admin-fixed-table">
            <colgroup><col style={{ width: '12%' }} /><col style={{ width: '12%' }} /><col style={{ width: '14%' }} /><col style={{ width: '42%' }} /><col style={{ width: '20%' }} /></colgroup>
            <thead><tr><th>{t.admin.memory?.day?.time}</th><th>{t.admin.memory?.kind}</th><th>{t.admin.memory?.day?.actor}</th><th>{t.admin.memory?.day?.happening}</th><th>{t.admin.memory?.day?.work}</th></tr></thead>
            <tbody>
              {(today.timeline || []).slice(0, timelineShown).map((item) => (
                <tr key={item.id}>
                  <td className="whitespace-nowrap">{formatTimestamp(item.at, { seconds: false })}</td>
                  <td>{t.admin.memory?.day?.kinds?.[item.kind] || item.kind}</td>
                  <td className="break-words">{item.who || item.actor || '-'}</td>
                  <td className="break-words"><span style={{ whiteSpace: 'pre-wrap' }}>{item.text}</span></td>
                  <td className="break-words">{item.work_item ? <button type="button" className="admin-link" onClick={() => writeAdminRoute('company-memory', 'graph', item.work_item)}>{item.title || item.work_item}</button> : (item.title || '-')}</td>
                </tr>
              ))}
              {!(today.timeline || []).length && today.loaded ? <tr><td colSpan="5">{t.admin.memory?.day?.empty}</td></tr> : null}
            </tbody>
          </table>
        </div></div>
        <ShowMore shown={timelineShown} total={(today.timeline || []).length} onMore={timelineMore} />
      </AdminSection>

      <AdminSection id="today-work" title={n.today?.workTitle} count={working.length} state={working.length ? '' : (today.loaded ? n.today?.workNone : '')}
        actions={<AdminLink tab="work" section="items">{n.pages?.work}</AdminLink>}>
        {working.length ? (
          <div className="admin-table-shell"><div className="admin-table-scroll">
            <table className="admin-table admin-fixed-table">
              <colgroup><col style={{ width: '18%' }} /><col style={{ width: '46%' }} /><col style={{ width: '12%' }} /><col style={{ width: '12%' }} /><col style={{ width: '12%' }} /></colgroup>
              <thead><tr><th>{n.work?.owner}</th><th>{n.work?.item}</th><th>{n.work?.priority}</th><th>{n.work?.due}</th><th>{n.work?.updated}</th></tr></thead>
              <tbody>
                {working.slice(0, 50).map((item) => (
                  <tr key={item.id}>
                    <td>{item.owner_kind === 'person' ? item.owner : botOf(item.owner)}</td>
                    <td className="break-words"><button type="button" className="admin-link" onClick={() => writeAdminRoute('company-memory', 'graph', item.id)}>{item.title || item.id}</button>{item.project ? <span className="text-text-secondary"> · {item.project}</span> : null}</td>
                    <td>{item.priority || '-'}</td>
                    <td>{item.due || '-'}</td>
                    <td>{formatTimestamp(item.updated_at, { seconds: false })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div></div>
        ) : null}
      </AdminSection>

      <AdminSection id="today-meetings" title={n.pages?.meetings}
        state={`${n.today?.nextMeeting}: ${String(nextMeetingHour).padStart(2, '0')}:00${lastMeeting ? ` · ${n.today?.lastMeeting}: ${formatTimestamp(lastMeeting.started_at, { seconds: false })}` : ''}`}
        actions={<AdminLink tab="meetings">{n.pages?.meetings}</AdminLink>}>
        {lastMeeting ? (
          <>
            {lastMeeting.summary ? <p className="text-sm" style={{ whiteSpace: 'pre-wrap' }}>{lastMeeting.summary}</p> : null}
            <h3>{n.meetings?.commitments}</h3>
            <ul className="admin-rows">
              {(Array.isArray(lastMeeting.commitments) ? lastMeeting.commitments : []).map((commitment, index) => (
                <li key={index} className="admin-row"><span><strong>{botOf(commitment.position)}</strong>: {commitment.what || commitment.text || '-'}</span></li>
              ))}
              {!(Array.isArray(lastMeeting.commitments) && lastMeeting.commitments.length) ? <li className="admin-row">{n.none}</li> : null}
            </ul>
          </>
        ) : <div className="text-sm text-text-secondary">{today.loaded ? n.meetings?.none : t.auth.validating}</div>}
      </AdminSection>

      <AdminSection id="today-state" title={n.stateTitle} count={stateLines.length}
        tone={stateLines.some((line) => line.tone === 'error') ? 'error' : stateLines.some((line) => line.tone === 'warning') ? 'warning' : 'ok'}>
        <div className="admin-attention-list">
          {stateLines.map((line) => stateRow(line.key, line.label, line.text, line.tab, line.section, line.tone))}
          {!stateLines.length ? <div className="homepage-muted-panel p-4 text-sm text-text-secondary">{t.auth.validating}</div> : null}
        </div>
      </AdminSection>

      <AdminSection id="today-services" title={t.admin.aiServicesTitle} subtitle={t.admin.aiServicesSubtitle} count={(services.services || []).length} collapsed>
        {sectionLoading.services ? <div className="homepage-muted-panel p-4 text-sm text-text-secondary">{t.auth.validating}</div> : null}
        {services.error ? <div className="homepage-muted-panel p-4 text-sm text-error">{services.error}</div> : null}
        <div className="admin-table-shell"><div className="admin-table-scroll">
          <table className="admin-table admin-fixed-table">
            <colgroup><col style={{ width: '26%' }} /><col style={{ width: '44%' }} /><col style={{ width: '30%' }} /></colgroup>
            <thead><tr><th>{n.service}</th><th>{t.admin.ops?.description}</th><th>{n.serviceLinks}</th></tr></thead>
            <tbody>
              {(services.services || []).map((service) => (
                <tr key={service.id}>
                  <td><div className="admin-user-name">{service.title}</div><div className="text-xs text-text-muted">{service.category}</div></td>
                  <td className="break-words">{service.description}</td>
                  <td className="admin-inline-actions">
                    {(service.links || []).map((link) => (
                      <a key={`${service.id}-${link.label}`} href={resolveDashboardLink(link)} target="_blank" rel="noreferrer" className="homepage-button-secondary">{link.label}</a>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div></div>
      </AdminSection>
    </>
  );

  // -- work: the board of open work items ----------------------------------------------------------------------------

  const workOwners = Array.from(new Set((today.workItems || []).map((item) => item.owner).filter(Boolean)));
  const boardItems = (today.workItems || []).filter((item) => !workOwnerFilter || item.owner === workOwnerFilter);
  const workPage = (
    <>
      {activeSection === 'items' ? (
        <AdminSection id="work-items" title={n.work?.title} subtitle={n.work?.subtitle} count={boardItems.length}
          actions={(
            <select className="form-input admin-inline-input" value={workOwnerFilter} onChange={(event) => setWorkOwnerFilter(event.target.value)}>
              <option value="">{n.work?.all}</option>
              {workOwners.map((owner) => <option key={owner} value={owner}>{botOf(owner)}</option>)}
            </select>
          )}>
          {today.error ? <div className="homepage-muted-panel p-4 text-sm text-error">{t.admin.memory?.unavailable} ({today.error})</div> : null}
          <div className="admin-table-shell"><div className="admin-table-scroll">
            <table className="admin-table admin-fixed-table">
              <colgroup><col style={{ width: '30%' }} /><col style={{ width: '8%' }} /><col style={{ width: '12%' }} /><col style={{ width: '10%' }} /><col style={{ width: '6%' }} /><col style={{ width: '8%' }} /><col style={{ width: '16%' }} /><col style={{ width: '10%' }} /></colgroup>
              <thead><tr><th>{n.work?.item}</th><th>{n.work?.kind}</th><th>{n.work?.owner}</th><th>{n.work?.status}</th><th>{n.work?.priority}</th><th>{n.work?.due}</th><th>{n.work?.scenarios}</th><th>{n.work?.updated}</th></tr></thead>
              <tbody>
                {boardItems.slice(0, workShown).map((item) => (
                  <tr key={item.id}>
                    <td className="break-words">
                      <button type="button" className="admin-link" onClick={() => writeAdminRoute('company-memory', 'graph', item.id)}>{item.title || item.id}</button>
                      <div className="text-xs text-text-muted">{item.id}{item.part_of ? ` · ${item.part_of}` : ''}{item.project ? ` · ${item.project}` : ''}{item.customer ? ` · ${item.customer}` : ''}{item.issue ? ` · ${item.issue}` : ''}{item.space_name ? ` · ${item.space_name}` : ''}</div>
                    </td>
                    <td>{item.kind || '-'}</td>
                    <td>{item.owner_kind === 'person' ? item.owner : botOf(item.owner)}</td>
                    <td>{t.admin.memory?.graph?.states?.[item.status] || item.status || '-'}</td>
                    <td>{item.priority || '-'}</td>
                    <td>{item.due || '-'}</td>
                    <td className="text-xs break-words">{(item.scenarios || []).length ? item.scenarios.map((scenario) => `${scenario.name}: ${t.admin.memory?.graph?.results?.[scenario.result] || scenario.result || 'pending'}`).join(' · ') : '-'}</td>
                    <td>{formatTimestamp(item.updated_at, { seconds: false })}</td>
                  </tr>
                ))}
                {!boardItems.length && today.loaded ? <tr><td colSpan="8">{n.work?.none}</td></tr> : null}
              </tbody>
            </table>
          </div></div>
          <ShowMore shown={workShown} total={boardItems.length} onMore={workMore} />
        </AdminSection>
      ) : null}
      {activeSection === 'projects' ? <SoftwareDevelopmentPage embedded view="projects" /> : null}
    </>
  );

  // -- meetings: the record of the colleagues' meetings -------------------------------------------------------------

  const meetingsPage = (
    <AdminSection id="meetings" title={n.pages?.meetings} subtitle={n.meetings?.subtitle} count={(today.meetings || []).length}
      state={`${n.today?.nextMeeting}: ${String(nextMeetingHour).padStart(2, '0')}:00`}
      actions={<button type="button" className="homepage-button-secondary" disabled={meetingBusy} onClick={holdMeeting}>{n.meetings?.hold}</button>}>
      {meetingNotice ? <div className="homepage-muted-panel p-3 text-sm text-text-secondary">{meetingNotice}</div> : null}
      <div className="admin-table-shell"><div className="admin-table-scroll">
        <table className="admin-table admin-fixed-table">
          <colgroup><col style={{ width: '12%' }} /><col style={{ width: '10%' }} /><col style={{ width: '12%' }} /><col style={{ width: '12%' }} /><col style={{ width: '30%' }} /><col style={{ width: '24%' }} /></colgroup>
          <thead><tr><th>{n.meetings?.slot}</th><th>{n.meetings?.kind}</th><th>{n.meetings?.started}</th><th>{n.meetings?.ended}</th><th>{n.meetings?.summary}</th><th>{n.meetings?.commitments}</th></tr></thead>
          <tbody>
            {(today.meetings || []).map((meeting) => (
              <tr key={meeting.id}>
                <td>{meeting.slot || '-'}</td>
                <td>{meeting.kind || '-'}</td>
                <td>{formatTimestamp(meeting.started_at, { seconds: false })}</td>
                <td>{formatTimestamp(meeting.ended_at, { seconds: false })}</td>
                <td className="break-words" style={{ whiteSpace: 'pre-wrap' }}>{meeting.summary || '-'}</td>
                <td className="break-words">
                  {(Array.isArray(meeting.commitments) ? meeting.commitments : []).map((commitment, index) => (
                    <div key={index}><strong>{botOf(commitment.position)}</strong>: {commitment.what || commitment.text || '-'}</div>
                  ))}
                  {!(Array.isArray(meeting.commitments) && meeting.commitments.length) ? n.none : null}
                </td>
              </tr>
            ))}
            {!(today.meetings || []).length ? <tr><td colSpan="6">{today.loaded ? n.meetings?.none : t.auth.validating}</td></tr> : null}
          </tbody>
        </table>
      </div></div>
    </AdminSection>
  );

  // -- the platform pages -------------------------------------------------------------------------------------------

  const gatewayPage = (
    <>
      <AdminSection id="gateway-state" title={t.admin.aiGatewayTitle} subtitle={t.admin.aiGatewaySubtitle}
        state={sectionLoading.aiGateway ? '...' : aiGateway.gateway?.programmed ? t.admin.statusConfigured : t.admin.statusMissing}
        tone={sectionLoading.aiGateway ? '' : aiGateway.gateway?.programmed ? 'ok' : 'error'}
        actions={<><AdminLink tab="engines">{t.admin.aiGatewayEngines}</AdminLink><AdminLink tab="usage" section="budgets">{t.admin.aiGatewayBudgets}</AdminLink></>}>
        {sectionLoading.aiGateway ? <div className="homepage-muted-panel p-4 text-sm text-text-secondary">{t.auth.validating}</div> : null}
        {aiGateway.error ? <div className="homepage-muted-panel p-4 text-sm text-error">{aiGateway.error}</div> : null}
        <FactGrid items={[
          [t.admin.aiGatewayEndpoint, aiGateway.gateway?.url || '-'],
          [t.admin.aiGatewayIdentitySource, aiGateway.identity?.claim || '-'],
          [t.admin.aiGatewayBudgetSource, <code>{aiGateway.budget_source || '-'}</code>],
          [t.admin.aiGatewayWindow, aiGateway.budget?.window || '-'],
        ]} />
        <p className="text-xs text-text-muted">{t.admin.aiGatewayHint}</p>
      </AdminSection>
      <AdminSection id="gateway-models" title={t.admin.aiGatewayModels} count={(aiGateway.models || []).length}>
        <div className="admin-table-shell">
          <div className="admin-table-scroll">
            <table className="admin-table admin-gateway-table">
              <colgroup><col className="admin-col-a" /><col className="admin-col-b" /><col className="admin-col-rest" /></colgroup>
              <thead><tr><th>{t.admin.aiGatewayModel}</th><th>{t.admin.aiGatewaySource}</th><th>{t.admin.aiGatewayBackend}</th></tr></thead>
              <tbody>
                {(aiGateway.models || []).map((entry) => (
                  <tr key={entry.model}><td className="admin-user-name admin-cell-clip" title={displayModelName(entry.model)}>{displayModelName(entry.model)}</td><td>{sourceLabel(entry.source)}</td><td className="admin-cell-clip" title={entry.backend || ''}>{entry.backend || '-'}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </AdminSection>
      <AdminSection id="gateway-backends" title={t.admin.aiGatewayBackends}
        state={readyOf((aiGateway.backends || []).filter((entry) => entry.ready >= entry.desired && entry.desired > 0).length, (aiGateway.backends || []).length)}
        tone={(aiGateway.backends || []).some((entry) => !(entry.ready >= entry.desired && entry.desired > 0)) ? 'error' : (aiGateway.backends || []).length ? 'ok' : ''}>
        <div className="admin-table-shell">
          <div className="admin-table-scroll">
            <table className="admin-table admin-gateway-table">
              <colgroup><col className="admin-col-a" /><col className="admin-col-b" /><col className="admin-col-rest" /></colgroup>
              <thead><tr><th>{t.admin.aiGatewayBackend}</th><th>{t.admin.aiGatewayServing}</th><th>{t.admin.platformImage}</th></tr></thead>
              <tbody>
                {(aiGateway.backends || []).map((entry) => (
                  <tr key={entry.name}>
                    <td className="admin-user-name admin-cell-clip" title={entry.name}>{entry.name}</td>
                    <td><span className={`admin-status-pill ${entry.ready >= entry.desired && entry.desired > 0 ? 'is-ok' : 'is-missing'}`}>{entry.ready} / {entry.desired}</span></td>
                    <td className="admin-user-mail admin-cell-clip" title={entry.image}>{entry.image}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </AdminSection>
    </>
  );

  const budgetRules = aiGateway.budget?.rules || [];
  const usagePage = (
    <>
      {activeSection === 'identities' ? (
        <AdminSection id="usage-identities" title={t.admin.aiUsageTitle} subtitle={t.admin.aiUsageSubtitle} count={(aiUsage.identities || []).length}
          state={`${t.admin.aiGatewayWindow}: ${sectionLoading.aiUsage ? '...' : aiUsage.window || '-'}${aiUsage.window_started_at ? ` · ${t.admin.aiUsageWindowStarted}: ${formatBerlin(aiUsage.window_started_at)}` : ''}`}
          actions={<AdminLink tab="engines">{t.admin.gpuIdentities}</AdminLink>}>
          {sectionLoading.aiUsage ? <div className="homepage-muted-panel p-4 text-sm text-text-secondary">{t.auth.validating}</div> : null}
          {aiUsage.error ? <div className="homepage-muted-panel p-4 text-sm text-error">{aiUsage.error}</div> : null}
          <div className="admin-table-shell">
            <div className="admin-table-scroll">
              <table className="admin-table admin-usage-table">
                <colgroup><col className="admin-col-identity" /><col className="admin-col-tokens" /><col className="admin-col-tokens" /><col className="admin-col-tokens" /><col className="admin-col-model" /><col className="admin-col-model" /><col className="admin-col-action" /></colgroup>
                <thead>
                  <tr>
                    <th>{t.admin.aiGatewayIdentity}</th>
                    <th>{t.admin.aiUsageTotalTokens}</th>
                    <th>{t.admin.aiGatewayTokenBudget}</th>
                    <th>{t.admin.aiUsageRemaining}</th>
                    <th>{t.admin.aiGatewayModel}</th>
                    <th>{t.admin.aiGatewaySource}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {(aiUsage.identities || []).map((entry) => (
                    <tr key={`${entry.identity}-${entry.rule}`}>
                      <td className="admin-user-name admin-cell-clip" title={entry.identity}>{entry.identity}</td>
                      <td>{formatInteger(entry.tokens)}</td>
                      <td>{entry.meter ? t.admin.aiGatewayMeter : entry.unlimited ? t.admin.aiBudgetUnlimited : formatInteger(entry.limit)}</td>
                      <td>{entry.meter ? '-' : entry.unlimited ? t.admin.aiBudgetUnlimited : formatInteger(entry.remaining)}</td>
                      <td>{entry.model === '*' ? t.admin.aiGatewayAnyModel : displayModelName(entry.model)}</td>
                      <td>{sourceLabel(entry.source)}</td>
                      <td>
                        <button className="homepage-button-secondary is-destructive" type="button" onClick={() => window.confirm(fill(n.confirmRemove, { what: `${t.admin.aiUsageReset}: ${entry.identity}` })) && resetIdentityWindow(entry.identity)}>
                          {t.admin.aiUsageReset}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </AdminSection>
      ) : null}
      {activeSection === 'budgets' ? (
        <>
          <AdminSection id="usage-budgets" title={t.admin.aiGatewayBudgets} count={budgetRules.length}
            state={`${t.admin.aiBudgetDefault}: ${aiGateway.budget?.default_tokens ? `${formatInteger(aiGateway.budget.default_tokens)} / ${aiGateway.budget?.window || '-'}` : t.admin.aiBudgetUnlimited}`}>
            <div className="admin-table-shell">
              <div className="admin-table-scroll">
                <table className="admin-table admin-gateway-table">
                  <colgroup><col className="admin-col-a" /><col className="admin-col-b" /><col className="admin-col-c" /><col className="admin-col-d" /></colgroup>
                  <thead>
                    <tr>
                      <th>{t.admin.aiGatewayIdentity}</th>
                      <th>{t.admin.aiGatewayModel}</th>
                      <th>{t.admin.aiGatewayTokenBudget}</th>
                      <th>{t.admin.aiGatewayWindow}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {budgetRules.map((rule) => (
                      <tr key={rule.index}>
                        <td className="admin-user-name admin-cell-clip" title={rule.identity}>{rule.meter ? t.admin.aiGatewayMeterIdentity : rule.identity === '*' ? t.admin.aiGatewayAnyIdentity : rule.identity}</td>
                        <td className="admin-cell-clip" title={displayModelName(rule.model)}>{rule.model === '*' ? t.admin.aiGatewayAnyModel : displayModelName(rule.model)}</td>
                        <td>{rule.meter ? t.admin.aiGatewayMeter : rule.unlimited ? t.admin.aiBudgetUnlimited : formatInteger(rule.tokens)}</td>
                        <td className="admin-inline-actions">
                          <span>{rule.window}</span>
                          {rule.identity !== '*' && rule.unlimited ? (
                            <button type="button" className="homepage-button-secondary" disabled={budgetSaving}
                              onClick={() => applyBudget({ scope: 'identity', identity: rule.identity, unlimited: false })}>
                              {t.admin.aiBudgetRestoreLimit}
                            </button>
                          ) : null}
                          {rule.identity !== '*' && !rule.unlimited ? (
                            <>
                              <button type="button" className="homepage-button-secondary" disabled={budgetSaving}
                                onClick={() => applyBudget({ scope: 'identity', identity: rule.identity, unlimited: true })}>
                                {t.admin.aiBudgetSetUnlimited}
                              </button>
                              <button type="button" className="homepage-button-secondary is-destructive" disabled={budgetSaving}
                                onClick={() => window.confirm(fill(n.confirmRemove, { what: `${t.admin.aiGatewayTokenBudget} ${rule.identity}` })) && applyBudget({ scope: 'identity', identity: rule.identity, remove: true })}>
                                {t.admin.aiBudgetRemove}
                              </button>
                            </>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </AdminSection>
          <AdminSection id="usage-budget-form" title={t.admin.aiBudgetControlTitle} subtitle={t.admin.aiBudgetControlSubtitle} collapsed>
            <div className="admin-pair">
              <div className="space-y-3">
                <label className="admin-field">{t.admin.aiBudgetDefault}
                  <input
                    className="form-input"
                    type="number"
                    min="1"
                    placeholder={String(aiGateway.budget?.default_tokens ?? '')}
                    value={budgetDraft.default}
                    onChange={(event) => setBudgetDraft((current) => ({ ...current, default: event.target.value }))}
                  />
                </label>
                <div className="admin-inline-actions">
                  <button className="homepage-button-primary" type="button" disabled={budgetSaving || !budgetDraft.default}
                    onClick={() => applyBudget({ scope: 'default', tokens: Number(budgetDraft.default) })}>
                    {t.admin.aiBudgetApply}
                  </button>
                  {aiGateway.budget?.default_tokens ? (
                    <button type="button" className="homepage-button-secondary" disabled={budgetSaving}
                      onClick={() => applyBudget({ scope: 'default', unlimited: true })}>{t.admin.aiBudgetSetUnlimited}</button>
                  ) : <span className="text-xs text-text-muted">{t.admin.aiBudgetUnlimited}</span>}
                </div>
              </div>
              <div className="space-y-3">
                <label className="admin-field">{t.admin.aiBudgetIdentity}
                  <input
                    className="form-input"
                    placeholder={t.admin.aiBudgetIdentityPlaceholder}
                    value={budgetDraft.identity}
                    onChange={(event) => setBudgetDraft((current) => ({ ...current, identity: event.target.value }))}
                  />
                </label>
                <label className="admin-field">{t.admin.aiBudgetTokens}
                  <input
                    className="form-input"
                    type="number"
                    min="1"
                    placeholder={t.admin.aiBudgetTokens}
                    value={budgetDraft.tokens}
                    onChange={(event) => setBudgetDraft((current) => ({ ...current, tokens: event.target.value }))}
                  />
                </label>
                <div className="admin-inline-actions">
                  <button className="homepage-button-primary" type="button" disabled={budgetSaving || !budgetDraft.identity.trim() || !budgetDraft.tokens}
                    onClick={() => applyBudget({ scope: 'identity', identity: budgetDraft.identity.trim(), tokens: Number(budgetDraft.tokens), window: aiGateway.budget?.window || 'Hour' })}>
                    {t.admin.aiBudgetAdd}
                  </button>
                  <button className="homepage-button-secondary" type="button" disabled={budgetSaving || !budgetDraft.identity.trim()}
                    onClick={() => applyBudget({ scope: 'identity', identity: budgetDraft.identity.trim(), unlimited: true })}>
                    {t.admin.aiBudgetSetUnlimited}
                  </button>
                </div>
              </div>
            </div>
            <div className="admin-inline-actions"><AdminLink tab="engines">{t.admin.gpuBudgetTitle}</AdminLink></div>
          </AdminSection>
        </>
      ) : null}
      {activeSection === 'company' ? <VeridionPaioneerPage embedded view="usage" /> : null}
      {activeSection === 'smtm' ? <VeridionPage embedded section="usage" /> : null}
    </>
  );

  const workloadsPage = (
    <AdminSection id="workloads" title={t.admin.platformWorkload} count={platform.summary?.workloads ?? 0}
      state={readyOf(platform.summary?.ready ?? 0, platform.summary?.workloads ?? 0)}
      tone={platform.error ? 'error' : (platform.summary?.degraded ?? 0) > 0 ? 'warning' : 'ok'}
      actions={<a className="homepage-button-secondary" href="/shinyproxy/admin" onClick={openAppHostPage}>{t.admin.platformHostPage}</a>}>
      {sectionLoading.platform ? <div className="homepage-muted-panel p-4 text-sm text-text-secondary">{t.auth.validating}</div> : null}
      {platform.error ? <div className="homepage-muted-panel p-4 text-sm text-error">{t.admin.platformUnavailable}: {platform.error}</div> : null}
      {!sectionLoading.platform && !platform.metrics_available ? <div className="homepage-muted-panel p-4 text-sm text-text-secondary">{t.admin.platformMetricsMissing}</div> : null}
      <FactGrid items={[
        [t.admin.smtmGeneratedAt, sectionLoading.platform ? '...' : formatTimestamp(platform.generated_at)],
        [t.admin.platformCpu, formatCores(platform.summary?.cpu_cores)],
        [t.admin.platformMemory, formatBytes(platform.summary?.memory_bytes)],
        [t.admin.platformRestartsTotal, formatInteger(platform.summary?.restarts ?? 0)],
      ]} />
      <div className="admin-table-shell">
        <div className="admin-table-scroll">
          <table className="admin-table admin-workload-table">
            <colgroup>
              <col className="admin-col-name" />
              <col className="admin-col-state" />
              <col className="admin-col-replicas" />
              <col className="admin-col-cpu" />
              <col className="admin-col-memory" />
              <col className="admin-col-restarts" />
              <col className="admin-col-image" />
            </colgroup>
            <thead>
              <tr>
                <th>{t.admin.platformWorkload}</th>
                <th>{t.admin.platformState}</th>
                <th>{t.admin.platformReplicas}</th>
                <th>{t.admin.platformCpu}</th>
                <th>{t.admin.platformMemory}</th>
                <th>{t.admin.platformRestarts}</th>
                <th>{t.admin.platformImage}</th>
              </tr>
            </thead>
            <tbody>
              {(platform.namespaces || []).map((entry) => (
                <React.Fragment key={entry.namespace}>
                  <tr className="admin-table-group">
                    <td colSpan="7">
                      <span className="admin-table-group-name">{entry.namespace}</span>
                      {entry.error ? <span className="admin-status-pill is-missing">{entry.error}</span> : null}
                      {!entry.error && !(entry.workloads || []).length ? <span className="admin-table-group-note">{t.admin.platformNoWorkloads}</span> : null}
                    </td>
                  </tr>
                  {(entry.workloads || []).map((workload) => (
                    <tr key={`${entry.namespace}-${workload.name}`}>
                      <td className="admin-user-name admin-cell-clip" title={workload.name}>{workload.name}</td>
                      <td><span className={`admin-status-pill ${platformStateClass(workload.state)}`}>{platformStateLabel(workload.state)}</span></td>
                      <td>{workload.ready} / {workload.desired}</td>
                      <td className="admin-user-created">{formatCores(workload.cpu_cores)}</td>
                      <td className="admin-user-created">{formatBytes(workload.memory_bytes)}</td>
                      <td className="admin-user-created">{formatInteger(workload.restarts)}</td>
                      <td className="admin-user-mail admin-cell-clip" title={workload.image}>{workload.image}</td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AdminSection>
  );

  const platformPage = (
    <>
      {activeSection === 'nodes' ? (
        <AdminSection id="platform-nodes" title={t.admin.platformNodes} count={(platform.nodes || []).length}
          state={readyOf((platform.nodes || []).filter((node) => node.ready).length, (platform.nodes || []).length)}
          tone={platform.error ? 'error' : (platform.nodes || []).some((node) => !node.ready || node.pressure?.length) ? 'warning' : 'ok'}
          actions={<span className="text-sm text-text-secondary">{t.admin.smtmGeneratedAt}: {sectionLoading.platform ? '...' : formatTimestamp(platform.generated_at)}</span>}>
          {sectionLoading.platform ? <div className="homepage-muted-panel p-4 text-sm text-text-secondary">{t.auth.validating}</div> : null}
          {platform.error ? <div className="homepage-muted-panel p-4 text-sm text-error">{t.admin.platformUnavailable}: {platform.error}</div> : null}
          <div className="admin-table-shell"><div className="admin-table-scroll">
            <table className="admin-table admin-fixed-table">
              <colgroup><col style={{ width: '16%' }} /><col style={{ width: '8%' }} /><col style={{ width: '14%' }} /><col style={{ width: '12%' }} /><col style={{ width: '10%' }} /><col style={{ width: '12%' }} /><col style={{ width: '12%' }} /><col style={{ width: '16%' }} /></colgroup>
              <thead><tr><th>{t.admin.platformNodes}</th><th>{t.admin.platformState}</th><th>{t.admin.platformOsImage}</th><th>{t.admin.platformKernel}</th><th>{t.admin.platformKubelet}</th><th>{t.admin.platformUsed}</th><th>{t.admin.platformReportedCapacity}</th><th>{t.admin.platformPressure}</th></tr></thead>
              <tbody>
                {(platform.nodes || []).map((node) => (
                  <tr key={node.name}>
                    <td><div className="admin-user-name">{node.name}</div><div className="admin-user-mail">{node.role} · {node.architecture}</div></td>
                    <td><span className={`admin-status-pill ${node.ready ? 'is-ok' : 'is-missing'}`}>{node.ready ? t.admin.platformStateReady : t.admin.platformStateDown}</span></td>
                    <td className="break-words">{node.os_image || '-'}<div className="text-xs text-text-muted">{t.admin.platformRuntime}: {node.container_runtime || '-'}</div></td>
                    <td className="break-words">{node.kernel || '-'}</td>
                    <td>{node.kubelet || '-'}</td>
                    <td>{formatCores(node.used_cpu_cores)} · {formatBytes(node.used_memory_bytes)}</td>
                    <td>{formatInteger(node.reported_cpu)} · {formatBytes(node.reported_memory_bytes)}{node.configured_cap ? <div className="text-xs text-text-muted">{t.admin.platformConfiguredCap}: {node.configured_cap}</div> : null}</td>
                    <td className="admin-smtm-issue-list">
                      {node.pressure?.length
                        ? node.pressure.map((item) => <span key={item} className="admin-status-pill is-missing">{item}</span>)
                        : <span className="admin-status-pill is-ok">{t.admin.platformPressureNone}</span>}
                    </td>
                  </tr>
                ))}
                {!(platform.nodes || []).length ? <tr><td colSpan="8">{t.auth.validating}</td></tr> : null}
              </tbody>
            </table>
          </div></div>
        </AdminSection>
      ) : null}
      {activeSection === 'events' ? (
        <AdminSection id="platform-events" title={t.admin.platformEvents} count={(platform.events || []).length} tone={(platform.events || []).length ? 'warning' : 'ok'} state={(platform.events || []).length ? '' : t.admin.platformEventsNone}>
          <div className="admin-table-shell">
            <div className="admin-table-scroll">
              <table className="admin-table admin-events-table">
                <colgroup>
                  <col className="admin-col-seen" />
                  <col className="admin-col-object" />
                  <col className="admin-col-reason" />
                  <col className="admin-col-message" />
                </colgroup>
                <thead>
                  <tr>
                    <th>{t.admin.platformEventLastSeen}</th>
                    <th>{t.admin.platformEventObject}</th>
                    <th>{t.admin.platformEventReason}</th>
                    <th>{t.admin.platformEventMessage}</th>
                  </tr>
                </thead>
                <tbody>
                  {(platform.events || []).slice(0, eventsShown).map((event, index) => (
                    <tr key={`${event.object}-${event.last_seen}-${index}`}>
                      <td className="admin-user-created">
                        <div>{formatDay(event.last_seen)}</div>
                        <div className="admin-user-mail">{formatMinutes(eventAgeMinutes(event.last_seen))}</div>
                      </td>
                      <td>
                        <div className="admin-user-name admin-cell-clip" title={event.object}>{event.object}</div>
                        <div className="admin-user-mail">{event.namespace}</div>
                      </td>
                      <td><span className="admin-status-pill is-warning">{event.reason}</span>{event.count > 1 ? ` ×${event.count}` : ''}</td>
                      <td className="text-xs text-text-secondary break-words">{event.message}</td>
                    </tr>
                  ))}
                  {!(platform.events || []).length ? <tr><td colSpan="4">{t.admin.platformEventsNone}</td></tr> : null}
                </tbody>
              </table>
            </div>
          </div>
          <ShowMore shown={eventsShown} total={(platform.events || []).length} onMore={eventsMore} />
        </AdminSection>
      ) : null}
    </>
  );

  const pageBody = {
    today: todayPage,
    organisation: <VeridionPaioneerPage embedded view="organisation" section={activeSection} />,
    gametheory: <VeridionGameTheoryPanel t={t} />,
    demos: <SoftwareDevelopmentPage embedded view="projects" />,
    llm: <LlmPaperPage section={activeSection} />,
    training: <TrainingDataFactoryPage section={activeSection} />,
    work: workPage,
    coding: <SoftwareDevelopmentPage embedded view="tasks" />,
    gitlab: activeSection === 'builds' ? <VeridionPaioneerPage embedded view="builds" /> : <SoftwareDevelopmentPage embedded view="gitlab" />,
    agents: <VeridionPaioneerPage embedded view={activeSection === 'designs' ? 'designs' : activeSection === 'definitions' ? 'definitions' : 'agents'} />,
    research: <MemoryDay fixedKind="research" title={n.research?.title} subtitle={n.research?.subtitle} />,
    webex: <VeridionPaioneerPage embedded view="webex" />,
    mailboxes: <VeridionMailboxesPanel t={t} />,
    conversations: <VeridionConversationsPanel t={t} />,
    meetings: meetingsPage,
    'company-memory': <CompanyMemoryPage embedded section={activeSection} />,
    knowledge: <VeridionPaioneerPage embedded view="knowledge" />,
    // The engines page carries the direct line to the models (owner UX decision): whoever looks at the
    // engines and the GPU talks to them right there, visible, not folded away (IDD D-HP-ADMIN-048).
    engines: (
      <div className="space-y-6">
        <SystemLlmAllocationPanel />
        <GpuPanel gpu={gpu.data} error={gpu.error} t={t} onBudget={applyBudget} saving={budgetSaving} labels={n} />
        <AdminSection id="engines-console" title={t.admin.core?.directLlmTitle} subtitle={t.admin.core?.directLlmSubtitle}>
          <LlmConsole t={t} />
        </AdminSection>
      </div>
    ),
    usage: usagePage,
    core: <VeridionPaioneerPage embedded view="core" />,
    workloads: workloadsPage,
    gateway: gatewayPage,
    platform: platformPage,
    orchestrator: <VeridionPaioneerPage embedded view="orchestrator" section={activeSection} />,
    smtm: smtmSections,
    agentic: <VeridionPage embedded section={activeSection} />,
    'agentic-rag': <AgenticRagPage embedded />,
    users: usersPage,
    mail: mailPage,
    audit: auditPage,
  }[activeAdminTab] || todayPage;
  // Pages that carry their own head: the memory, the orchestrator and the parked application.
  const ownHead = ['company-memory', 'orchestrator', 'agentic-rag'].includes(activeAdminTab);

  return (
    <AdminLabelsContext.Provider value={labelsForBlocks}>
      <main className="site-main admin-dashboard-main">
        <div className="admin-shell">
          <AdminSideNav
            title={t.admin.title}
            kicker={t.admin.adminKicker}
            groups={tabGroups}
            active={activeAdminTab}
            activeSection={activeSection}
            sectionGroups={sectionGroups}
            onSelectPage={selectTab}
            onSelectSection={(section, tab) => writeAdminRoute(tab || activeAdminTab, section)}
            badges={tabBadges}
            sectionBadges={sectionBadges}
          />
          <div className="admin-content admin-page-body">
            {message ? <div className="homepage-muted-panel p-4 text-sm text-text-secondary">{message}</div> : null}
            {translatedError ? <div className="homepage-muted-panel p-4 text-sm text-error">{translatedError}</div> : null}
            {!ownHead ? <AdminPageHead title={pageTitle} subtitle={subtitleOf} actions={activeAdminTab === 'today' ? <a className="homepage-button-secondary" href="https://visitor.paioneer-labs.eu" target="_blank" rel="noreferrer">{t.admin.openVisitorMonitoring}</a> : null} /> : null}
            {pageBody}
          </div>
        </div>
      </main>
    </AdminLabelsContext.Provider>
  );
};

export default AdminPage;
